// /admin — admin dashboard. Tabs: Products | Orders | Messages | Customers.
import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Shield, ShieldOff, Package, ListOrdered, Mail, Users, Plus, Trash2, Loader2, Edit3, Image as ImageIcon, Settings, Send, MessageCircle, Printer, Bell, Tag, Megaphone, Info, TrendingUp, BarChart3, Calendar, ShoppingCart, Search, X, ExternalLink, Ban, Star, Eye, EyeOff, FileText, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Check, Download, Upload, Store, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { asset } from "@/lib/siteAssets";
import { track } from "@/lib/analytics";
import { POLICY_SEED_CONTENT } from "@/lib/policySeedContent";
import { playNewOrderChime, isOrderSoundMuted, setOrderSoundMuted, primeOrderSoundUnlock } from "@/lib/notificationSound";
import {
  useProducts,
  useAllOrders,
  useContactMessages,
  deleteContactMessage,
  useAllCustomers,
  saveProduct,
  deleteProduct,
  seedProductsToFirestore,
  uploadProductImage,
  updateOrderStatus,
  updateOrderCourier,
  useCheckoutSettings,
  updateCheckoutSettings,
  useOrderMessages,
  sendOrderMessage,
  useNotifications,
  createNotification,
  deleteNotification,
  useAllReviews,
  setReviewVisibility,
  useCostSheet,
  saveCostSheet,
  syncProductPricesFromCostSheet,
  updateCustomer,
  deleteCustomer,
  createStoreAssociate,
  updateStoreAssociate,
  useSiteContent,
  saveSiteContent,
  DEFAULT_SITE_CONTENT,
  usePolicyContent,
  savePolicyContent,
  DEFAULT_POLICY_CONTENT,
  DEFAULT_CHECKOUT_SETTINGS,
  DEFAULT_STATE_SHIPPING_RATES,
  useAllOffers,
  saveOffer,
  deleteOffer,
  useComboSettings,
  updateComboSettings,
  DEFAULT_COMBO_SETTINGS,
  useSamplePackSettings,
  updateSamplePackSettings,
  DEFAULT_SAMPLE_PACK_SETTINGS,
} from "@/lib/firestoreService";
import { compressImage } from "@/lib/imageCompress";
import { ORDER_STATUSES, CANCELLED_STATUS, findStatus } from "@/lib/seedData";
import { formatPrice } from "@/lib/cart";
import { printOrderLabel } from "@/lib/printLabel";
import { useLang } from "@/lib/i18n";
import { fulfillmentMeta, requiresCourierOnDispatch, isPickupOrder, isStoreOrder, PAYMENT_METHOD_LABELS } from "@/lib/fulfillment";
import { associateStoreStats, validateStorePassword, validateStoreUserId } from "@/lib/storeAssociate";
import { orderNumber } from "@/lib/orderNumber";

// Admin sees "Order Received" for a freshly placed order — customers still
// see "Order Created" (seedData.js) since that's a separate, more casual
// audience-facing wording the user wanted kept as-is.
// `order` is optional — pass it when labeling a specific order's status so
// pickup orders show "Ready for Pickup" instead of the generic courier
// wording "Ready for Dispatch" (matches the customer-facing timeline).
const ADMIN_TAB_EVENT_LABELS = {
  orders: "Orders", accounting: "Accounting", products: "Products", users: "Users",
  messages: "Messages", notifications: "Notifications", reviews: "Reviews",
  content: "Site Settings", settings: "Charges and Tax", offers: "Offers",
  combos: "Combos and Packs",
};

const ADMIN_STATUS_LABEL_OVERRIDES = { order_created: "Order Received" };
const adminStatusLabel = (key, fallbackLabel, order) => {
  if (order && key === "ready_dispatch" && isPickupOrder(order)) return "Ready for Pickup";
  return ADMIN_STATUS_LABEL_OVERRIDES[key] || fallbackLabel;
};

export const AdminPage = ({ onAuthOpen }) => {
  const { t } = useLang();
  const { user, isAdmin, isSuperAdmin, firebaseReady: ready, loading } = useAuth();
  const [tab, setTab] = useState("orders");
  // Track which tabs have been visited so they stay mounted (preserving Firestore listeners)
  // "orders" is pre-visited so it loads immediately on dashboard open
  const [visited, setVisited] = useState(() => new Set(["orders"]));

  // Always subscribed (like notifications) so the tab badge below is accurate
  // even before the admin has opened the Reviews tab.
  const reviews = useAllReviews() || [];
  const [reviewsLastSeen, setReviewsLastSeen] = useState(
    () => parseInt(localStorage.getItem("admin_reviews_last_seen") || "0")
  );
  const newReviewsCount = reviews.filter(
    (r) => (r.createdAt?.seconds || 0) * 1000 > reviewsLastSeen
  ).length;

  // Lifted here (rather than inside OrdersAdmin) so it's a single shared
  // subscription used for the Orders tab itself, the "New Orders" badge,
  // and the new-order celebration popup below — all need the same live list
  // regardless of which tab is currently active.
  const ordersRaw = useAllOrders();
  const orders = useMemo(() => ordersRaw || [], [ordersRaw]);
  const newOrdersCount = orders.filter((o) => o.status === "order_created" && o.order_source !== "store").length;
  const [ordersSubTab, setOrdersSubTab] = useState("all");
  const [ordersSourceTab, setOrdersSourceTab] = useState("all");
  const [celebration, setCelebration] = useState(null);
  const seenOrderIdsRef = useRef(null);

  // Read via a ref (not state) inside the order-detection effect below so
  // toggling the mute button doesn't need to be a dependency of — and
  // doesn't re-run — that effect.
  const [soundMuted, setSoundMuted] = useState(() => isOrderSoundMuted());
  const soundMutedRef = useRef(soundMuted);
  useEffect(() => { soundMutedRef.current = soundMuted; }, [soundMuted]);
  const toggleOrderSound = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    setOrderSoundMuted(next);
    track("admin_order_sound_toggled", { muted: next });
  };

  // Unlocks the (silent-until-a-real-gesture) AudioContext on the very
  // first click/keydown anywhere in the dashboard, so a later new-order
  // event — which is not itself a user gesture — can actually play sound
  // instead of being silently blocked by the browser's autoplay policy.
  useEffect(() => {
    const unlock = () => primeOrderSoundUnlock();
    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const currentIds = new Set(orders.map((o) => o.id));
    if (seenOrderIdsRef.current === null) {
      // First load — just record what already exists, nothing to celebrate.
      seenOrderIdsRef.current = currentIds;
      return;
    }
    // A brand-new order lands at "order_created" (manual UPI/QR flow) or
    // straight at "order_confirmed" (PhonePe — payment's already verified,
    // so it skips the manual-review step entirely). Both are "a customer
    // just placed an order" from the admin's point of view; only walk-in/
    // store sales (already delivered on the spot) don't need a heads-up.
    const newOnes = orders.filter((o) =>
      !seenOrderIdsRef.current.has(o.id) &&
      o.order_source !== "store" &&
      (o.status === "order_created" || o.status === "order_confirmed")
    );
    seenOrderIdsRef.current = currentIds;
    if (newOnes.length > 0) {
      setCelebration(newOnes[0]);
      if (!soundMutedRef.current) playNewOrderChime();
    }
  }, [orders]);

  const canAccessTab = (key) =>
    isSuperAdmin || key === "orders" || key === "messages" || key === "notifications";

  const switchTab = (key) => {
    if (!canAccessTab(key)) return;
    setTab(key);
    setVisited(v => { const n = new Set(v); n.add(key); return n; });
    // "accounting", "users", "offers", and "content" fire their own enriched
    // events from their components once data is loaded — skipped here to
    // avoid bare duplicates with no useful data.
    if (key !== "accounting" && key !== "users" && key !== "offers" && key !== "content") {
      track(`Viewed ${ADMIN_TAB_EVENT_LABELS[key] || key} Tab`, { source: "admin_tab_nav" });
    }
    if (key === "reviews") {
      const now = Date.now();
      localStorage.setItem("admin_reviews_last_seen", String(now));
      setReviewsLastSeen(now);
    }
  };

  // "orders" is the default tab and never goes through switchTab() on mount —
  // track it directly so the very first thing an admin sees is still in the
  // event feed. Waits for ordersRaw to load (non-null) so the per-status
  // counts reflect real data, not the empty initial state.
  const ordersTabEventSentRef = useRef(false);
  useEffect(() => {
    if (!isAdmin || ordersTabEventSentRef.current || ordersRaw === null) return;
    ordersTabEventSentRef.current = true;
    const allStatuses = [...ORDER_STATUSES, CANCELLED_STATUS];
    const statusCounts = allStatuses.reduce((acc, s) => {
      acc[`orders_${s.key}`] = orders.filter((o) => o.status === s.key).length;
      return acc;
    }, {});
    track("Viewed Orders Tab", {
      source: "admin_tab_nav_initial",
      orders_total: orders.length,
      ...statusCounts,
    });
  }, [isAdmin, ordersRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canAccessTab(tab)) {
      setTab("orders");
    }
    setVisited((prev) => {
      let changed = false;
      const next = new Set();
      for (const key of prev) {
        if (canAccessTab(key)) next.add(key);
        else changed = true;
      }
      if (!next.has("orders")) {
        next.add("orders");
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [tab, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) {
    return (
      <Wrap>
        <Card>
          <div className="font-display text-2xl text-[#5A2D1D] mb-2">{t.admin.no_admin}</div>
          <p className="text-[#2E1A12]/80">Add your Firebase config to <code>frontend/.env</code> to enable admin features.</p>
        </Card>
      </Wrap>
    );
  }
  if (loading) return <Wrap><Card>{t.common.loading}</Card></Wrap>;
  if (!user) {
    return (
      <Wrap>
        <Card>
          <Shield className="w-10 h-10 mx-auto mb-3 text-[#5A2D1D]" />
          <div className="font-display text-2xl text-[#5A2D1D] mb-2">Sign in required</div>
          <button
            data-testid="admin-signin-button"
            onClick={onAuthOpen}
            className="mt-3 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold"
          >
            {t.nav.login}
          </button>
        </Card>
      </Wrap>
    );
  }
  if (!isAdmin) {
    return (
      <Wrap>
        <Card>
          <Shield className="w-10 h-10 mx-auto mb-3 text-[#5A2D1D]" />
          <div className="font-display text-2xl text-[#5A2D1D] mb-2" data-testid="admin-access-denied">
            {t.admin.access_denied}
          </div>
          <div className="text-sm text-[#2E1A12]/70">
            Signed in as <b>{user.email}</b>. To grant admin access, open Firebase Console → Firestore → <code className="bg-[#5A2D1D]/10 px-1">customers/{"{uid}"}</code> and set <code className="bg-[#5A2D1D]/10 px-1">isAdmin: true</code>.
          </div>
          <Link to="/" className="mt-4 inline-block text-[#556B2F] font-semibold hover:underline">
            ← Back to home
          </Link>
        </Card>
      </Wrap>
    );
  }

  const ALL_TABS = [
    { key: "orders",        label: t.admin.orders,    icon: ListOrdered, badge: newOrdersCount },
    { key: "accounting",    label: "Accounting",      icon: BarChart3 },
    { key: "products",      label: t.admin.products,  icon: Package },
    { key: "users",         label: "Users",           icon: Users },
    { key: "messages",      label: t.admin.messages,  icon: Mail },
    { key: "notifications", label: "Notifications",   icon: Bell },
    { key: "reviews",       label: "Reviews",         icon: Star, badge: newReviewsCount },
    { key: "content",       label: "Site Settings",   icon: FileText },
    { key: "settings",      label: "Charges and Tax", icon: Settings },
    { key: "offers",        label: "Offers",          icon: Tag },
    { key: "combos",        label: "Combos & Packs",  icon: ShoppingCart },
  ];
  const TABS = ALL_TABS.filter((tb) => canAccessTab(tb.key));

  return (
    <section data-testid="admin-page" className="py-3 sm:py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-[#556B2F]" />
          <span className="font-display text-base font-bold text-[#5A2D1D]">
            {isSuperAdmin ? "Annapurna Super Admin" : "Annapurna Admin"}
          </span>
          <span className="text-[#2E1A12]/50 text-xs">·</span>
          <span className="text-[#2E1A12]/60 text-xs truncate max-w-[200px]">{user.email}</span>
          <button
            data-testid="admin-order-sound-toggle"
            onClick={toggleOrderSound}
            title={soundMuted ? "Unmute new-order sound" : "Mute new-order sound"}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#5A2D1D]/70 hover:text-[#5A2D1D] border border-[#5A2D1D]/20 hover:border-[#5A2D1D]/40 rounded-full px-2.5 py-1 transition-colors"
          >
            {soundMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
        </div>

        <div className="mt-3 border-b border-[#5A2D1D]/20" data-testid="admin-tabs">
          <div className="flex flex-wrap gap-0.5">
            {TABS.map((tb) => {
              const I = tb.icon;
              const active = tab === tb.key;
              return (
                <button
                  key={tb.key}
                  data-testid={`admin-tab-${tb.key}`}
                  onClick={() => switchTab(tb.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-t-sm text-xs font-semibold border-b-2 transition-colors ${
                    active
                      ? "border-[#5A2D1D] text-[#5A2D1D] bg-[#F5E6CC]"
                      : "border-transparent text-[#2E1A12]/60 hover:text-[#5A2D1D]"
                  }`}
                >
                  <I className="w-3.5 h-3.5" />
                  {tb.label}
                  {!!tb.badge && (
                    <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center leading-none">
                      {tb.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          {/* Orders always mounts on page load (default tab).
              All other tabs mount on first click and stay mounted — no refetch on switch. */}
          <div className={tab === "orders"        ? "" : "hidden"}><OrdersAdmin orders={orders} subTab={ordersSubTab} setSubTab={setOrdersSubTab} sourceTab={ordersSourceTab} setSourceTab={setOrdersSourceTab} /></div>
          {canAccessTab("accounting")    && visited.has("accounting")    && <div className={tab === "accounting"    ? "" : "hidden"}><AccountingAdmin orders={orders} /></div>}
          {canAccessTab("products")      && visited.has("products")      && <div className={tab === "products"      ? "" : "hidden"}><ProductsAdmin /></div>}
          {canAccessTab("users")         && visited.has("users")         && <div className={tab === "users"         ? "" : "hidden"}><UsersAdmin orders={orders} /></div>}
          {visited.has("messages")      && <div className={tab === "messages"      ? "" : "hidden"}><MessagesAdmin /></div>}
          {visited.has("notifications") && <div className={tab === "notifications" ? "" : "hidden"}><NotificationsAdmin /></div>}
          {canAccessTab("reviews")       && visited.has("reviews")       && <div className={tab === "reviews"       ? "" : "hidden"}><ReviewsAdmin reviews={reviews} /></div>}
          {canAccessTab("content")       && visited.has("content")       && <div className={tab === "content"       ? "" : "hidden"}><SiteContentAdmin /></div>}
          {canAccessTab("settings")      && visited.has("settings")      && <div className={tab === "settings"      ? "" : "hidden"}><SettingsAdmin /></div>}
          {canAccessTab("offers")        && visited.has("offers")        && <div className={tab === "offers"        ? "" : "hidden"}><OffersAdmin /></div>}
          {canAccessTab("combos")        && visited.has("combos")        && <div className={tab === "combos"        ? "" : "hidden"}><CombosAndPacksAdmin /></div>}
        </div>
      </div>

      {celebration && (
        <NewOrderCelebration
          order={celebration}
          onClick={() => {
            switchTab("orders");
            // Jump to whichever status sub-tab the order actually landed at
            // (order_created for manual UPI/QR, order_confirmed for PhonePe)
            // rather than a hardcoded key that may not match — and reset the
            // source tab to "all" so the order is guaranteed to be visible
            // regardless of whether it's an online or in-store sale.
            setOrdersSourceTab("all");
            setOrdersSubTab(celebration.status || "all");
            setCelebration(null);
          }}
          onClose={() => setCelebration(null)}
        />
      )}
    </section>
  );
};

// Small "1-inch" celebratory popup in the corner when a brand-new order
// arrives, regardless of which tab the admin currently has open.
const NewOrderCelebration = ({ order, onClick, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 8000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      data-testid="new-order-celebration"
      onClick={onClick}
      className="fixed top-20 right-5 z-[80] w-28 cursor-pointer"
      style={{ animation: "celebration-pop 0.4s ease-out" }}
    >
      <div className="relative w-full h-28 rounded-sm shadow-2xl overflow-hidden bg-[#5A2D1D]">
        <img src={asset("celebration.gif")} alt="New order!" className="w-full h-full object-cover" />
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-0.5 right-0.5 bg-black/40 rounded-full p-0.5 text-white/80 hover:text-white"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="mt-1.5 bg-[#5A2D1D] text-[#F5E6CC] text-[10px] font-bold text-center py-1 rounded-sm shadow-lg">
        New Order Received!
      </div>
      <style>{`
        @keyframes celebration-pop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ---------------- Products tab ----------------
const blankProduct = {
  slug: "",
  name: "",
  name_te: "",
  description: "",
  description_te: "",
  category: "veg",
  image_url: "",
  featured: false,
  in_stock: true,
  stock_note: "",
  visible: true,
  sort: 99,
  sizes: [
    { label: "250gm", price: 0 },
    { label: "500gm", price: 0 },
    { label: "1000gm", price: 0 },
  ],
};

const ProductsAdmin = () => {
  const products = useProducts();
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const doSeed = async () => {
    setSeeding(true);
    try {
      await seedProductsToFirestore();
      track("Seeded Sample Products");
      toast.success("Sample products loaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-2xl font-bold text-[#5A2D1D]">Products</h2>
        <div className="flex gap-2">
          <button
            data-testid="admin-seed-products"
            onClick={doSeed}
            disabled={seeding}
            className="inline-flex items-center gap-2 border-2 border-[#5A2D1D]/40 hover:border-[#5A2D1D] text-[#5A2D1D] px-4 py-2 rounded-sm font-semibold text-sm"
          >
            {seeding && <Loader2 className="w-4 h-4 animate-spin" />}
            Seed Sample Products
          </button>
          <button
            data-testid="admin-add-product"
            onClick={() => { setEditing(blankProduct); track("Opened Add Product Form"); }}
            className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-4 py-2 rounded-sm font-semibold text-sm"
          >
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      {products === null ? (
        <div className="py-6 text-center text-[#5A2D1D]/70">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-products-grid">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.slug}`}
              className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm overflow-hidden"
            >
              <div className="relative aspect-[16/10] bg-[#EFD9B4] overflow-hidden">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} loading="lazy" decoding="async" className={`w-full h-full object-cover ${p.in_stock === false || p.visible === false ? "grayscale opacity-60" : ""}`} />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[#5A2D1D]/40"><ImageIcon /></div>
                )}
                <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
                  {p.in_stock === false && (
                    <div className="bg-[#7A3B25] text-[#F5E6CC] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">
                      Sold Out
                    </div>
                  )}
                  {p.visible === false && (
                    <div className="bg-[#2E1A12] text-[#F5E6CC] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm">
                      Hidden
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="font-display font-bold text-[#5A2D1D]">{p.name}</div>
                <div className="text-xs text-[#556B2F] uppercase font-bold mt-1">{p.category}</div>
                <div className="text-sm text-[#2E1A12]/70 mt-1 line-clamp-2">{p.description}</div>
                {p.in_stock === false && p.stock_note && (
                  <div className="text-xs text-[#7A3B25] mt-1.5 italic">"{p.stock_note}"</div>
                )}
                <div className="flex flex-wrap gap-1 mt-2 text-xs">
                  {p.sizes?.map((s) => (
                    <span key={s.label} className="bg-[#5A2D1D]/10 px-2 py-0.5 rounded-sm font-semibold">
                      {s.label} {formatPrice(s.price)}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button
                    data-testid={`admin-toggle-stock-${p.slug}`}
                    onClick={async () => {
                      try {
                        await saveProduct({ ...p, in_stock: p.in_stock === false });
                        track("product_stock_toggled", { product_id: p.id, product_name: p.name, in_stock: p.in_stock === false });
                        toast.success(p.in_stock === false ? "Marked in stock" : "Marked sold out");
                      } catch (e) {
                        toast.error(e.message);
                      }
                    }}
                    className={`inline-flex items-center gap-1 text-sm border px-3 py-1.5 rounded-sm font-semibold ${
                      p.in_stock === false
                        ? "border-[#556B2F]/40 text-[#556B2F] hover:bg-[#556B2F]/10"
                        : "border-[#7A3B25]/40 text-[#7A3B25] hover:bg-[#7A3B25]/10"
                    }`}
                  >
                    {p.in_stock === false ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    {p.in_stock === false ? "Mark In Stock" : "Mark Sold Out"}
                  </button>
                  <button
                    data-testid={`admin-toggle-visible-${p.slug}`}
                    onClick={async () => {
                      try {
                        await saveProduct({ ...p, visible: p.visible === false });
                        track("product_visibility_toggled", { product_id: p.id, product_name: p.name, visible: p.visible === false });
                        toast.success(p.visible === false ? "Now visible to customers" : "Hidden from customers");
                      } catch (e) {
                        toast.error(e.message);
                      }
                    }}
                    className={`inline-flex items-center gap-1 text-sm border px-3 py-1.5 rounded-sm font-semibold ${
                      p.visible === false
                        ? "border-[#556B2F]/40 text-[#556B2F] hover:bg-[#556B2F]/10"
                        : "border-[#7A3B25]/40 text-[#7A3B25] hover:bg-[#7A3B25]/10"
                    }`}
                  >
                    {p.visible === false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {p.visible === false ? "Show" : "Hide"}
                  </button>
                  <button
                    data-testid={`admin-edit-${p.slug}`}
                    onClick={() => { setEditing(p); track("Opened Edit Product Form", { product_id: p.id, product_name: p.name }); }}
                    title="Edit"
                    aria-label="Edit"
                    className="inline-flex items-center justify-center border border-[#5A2D1D]/30 hover:bg-[#5A2D1D]/5 p-1.5 rounded-sm text-[#5A2D1D]"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`admin-delete-${p.slug}`}
                    onClick={async () => {
                      const productProps = {
                        product_id:   p.id,
                        product_name: p.name,
                        product_slug: p.slug,
                        category:     p.category,
                        in_stock:     p.in_stock,
                        featured:     p.featured,
                        visible:      p.visible,
                        size_count:   (p.sizes || []).length,
                      };
                      track("Clicked Delete Product", productProps);
                      if (!window.confirm(`Delete ${p.name}?`)) {
                        track("Cancelled Delete Product", productProps);
                        return;
                      }
                      try {
                        await deleteProduct(p.id);
                        track("Deleted Product", productProps);
                        toast.success("Deleted");
                      } catch (e) {
                        track("Delete Product Failed", { ...productProps, error: e.message });
                        toast.error(e.message);
                      }
                    }}
                    title="Delete"
                    aria-label="Delete"
                    className="inline-flex items-center justify-center border border-[#5A2D1D]/30 hover:bg-[#7A3B25]/10 p-1.5 rounded-sm text-[#7A3B25]"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductFormModal initial={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};

const ProductFormModal = ({ initial, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !initial.id;

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const optimized = await compressImage(file);
      const url = await uploadProductImage(optimized, form.slug || null);
      setForm((f) => ({ ...f, image_url: url }));
      track("Uploaded Product Image", { is_new_product: isNew });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error("Name and slug required");
      return;
    }
    setSaving(true);
    try {
      const id = form.id || form.slug;
      // Prices are kept as whatever raw string the admin typed while editing
      // (see setSize below) so the field can be cleared to blank — coerce to
      // a clean number only now, at save time.
      const sizes = form.sizes.map((s) => ({ ...s, price: Number(s.price) || 0 }));
      await saveProduct({ ...form, id, sizes, sort: Number(form.sort) || 0 });
      track(isNew ? "product_created" : "product_updated", {
        product_id: id,
        product_name: form.name,
        category: form.category,
        in_stock: form.in_stock !== false,
        visible: form.visible !== false,
        size_count: sizes.length,
        min_price: sizes.length ? Math.min(...sizes.map(s => s.price)) : null,
        max_price: sizes.length ? Math.max(...sizes.map(s => s.price)) : null,
      });
      toast.success(isNew ? "Product created" : "Saved");
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Storing the coerced Number on every keystroke meant clearing the field
  // to type a fresh value immediately snapped back to "0" (Number("") || 0),
  // so the input could never actually go blank. Keep the raw string in state
  // while editing; onSave above converts it to a real number.
  const setSize = (i, key, val) => {
    const sizes = [...form.sizes];
    sizes[i] = { ...sizes[i], [key]: val };
    setForm({ ...form, sizes });
  };

  return (
    <div
      data-testid="product-form-modal"
      className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-2xl font-bold text-[#5A2D1D] mb-5">
          {isNew ? "New Product" : `Edit: ${initial.name}`}
        </h3>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Slug (unique)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required testid="form-slug" />
          <Field label="Sort order" type="number" value={form.sort} onChange={(v) => setForm({ ...form, sort: v })} testid="form-sort" />
          <Field label="Name (English)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="form-name" />
          <Field label="Name (Telugu)" value={form.name_te} onChange={(v) => setForm({ ...form, name_te: v })} testid="form-name-te" />
        </div>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Description (English)</label>
          <textarea
            data-testid="form-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
        </div>
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Description (Telugu)</label>
          <textarea
            data-testid="form-description-te"
            value={form.description_te}
            onChange={(e) => setForm({ ...form, description_te: e.target.value })}
            rows={2}
            className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Category</label>
            <select
              data-testid="form-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            >
              <option value="veg">Vegetarian</option>
              <option value="non-veg">Non-Vegetarian</option>
            </select>
          </div>
          <label className="flex items-end gap-2 cursor-pointer pb-2">
            <input
              data-testid="form-featured"
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />
            <span className="text-sm font-semibold">Featured / Bestseller</span>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="form-in-stock"
              type="checkbox"
              checked={form.in_stock !== false}
              onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
            />
            <span className="text-sm font-semibold">In Stock</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="form-visible"
              type="checkbox"
              checked={form.visible !== false}
              onChange={(e) => setForm({ ...form, visible: e.target.checked })}
            />
            <span className="text-sm font-semibold">Visible to customers</span>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {form.in_stock === false && (
            <Field
              label="Sold-out note (optional, shown to customers)"
              value={form.stock_note || ""}
              onChange={(v) => setForm({ ...form, stock_note: v })}
              placeholder="e.g. Back in stock next week"
              testid="form-stock-note"
            />
          )}
        </div>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Image URL</label>
          <div className="flex gap-2 mt-1">
            <input
              data-testid="form-image-url"
              value={form.image_url || ""}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="https://... (or use Upload)"
              className="flex-1 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
            <label className="cursor-pointer inline-flex items-center gap-1.5 border-2 border-[#5A2D1D]/40 hover:border-[#5A2D1D] px-3 py-2 rounded-sm text-sm font-semibold text-[#5A2D1D]">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              Upload
              <input
                data-testid="form-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
            </label>
          </div>
          {form.image_url && (
            <div className="mt-3 aspect-[16/9] w-full max-w-xs overflow-hidden rounded-sm bg-[#EFD9B4]">
              <img src={form.image_url} alt="preview" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-2">Sizes & Prices</div>
          <div className="space-y-2">
            {form.sizes.map((s, i) => (
              <div key={i} className="flex gap-2">
                <input
                  data-testid={`form-size-label-${i}`}
                  value={s.label}
                  onChange={(e) => setSize(i, "label", e.target.value)}
                  placeholder="Label (e.g. 250gm)"
                  className="flex-1 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
                <input
                  data-testid={`form-size-price-${i}`}
                  type="number"
                  value={s.price}
                  onChange={(e) => setSize(i, "price", e.target.value)}
                  placeholder="Price ₹"
                  className="w-32 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Product Details (shown in View Details popup) ── */}
        <div className="mt-6 border-t border-[#5A2D1D]/15 pt-5">
          <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-3">
            Product Details <span className="text-[#2E1A12]/40 normal-case tracking-normal font-normal">(shown in "View Details" popup)</span>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">
                Highlights <span className="text-[#2E1A12]/40 normal-case tracking-normal font-normal">— one per line</span>
              </label>
              <textarea
                value={(form.details?.highlights || []).join("\n")}
                onChange={(e) => {
                  const lines = e.target.value.split("\n").map(l => l.trimStart()).filter(Boolean);
                  setForm({ ...form, details: { ...(form.details || {}), highlights: e.target.value.split("\n").map(l => l.trimStart()) } });
                }}
                rows={3}
                placeholder={"No preservatives\nTraditional recipe\nMade fresh"}
                className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Ingredients</label>
                <textarea
                  value={form.details?.ingredients || ""}
                  onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), ingredients: e.target.value } })}
                  rows={2}
                  placeholder="Chicken, Red chilli, Salt, Sunflower oil..."
                  className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Ingredients (Telugu)</label>
                <textarea
                  value={form.details?.ingredients_te || ""}
                  onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), ingredients_te: e.target.value } })}
                  rows={2}
                  placeholder="చికెన్, కారం పొడి, ఉప్పు, సన్‌ఫ్లవర్ నూనె..."
                  className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Shelf Life</label>
                <input
                  value={form.details?.shelf_life || ""}
                  onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), shelf_life: e.target.value } })}
                  placeholder="6 months refrigerated"
                  className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">How to Serve</label>
                <input
                  value={form.details?.how_to_use || ""}
                  onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), how_to_use: e.target.value } })}
                  placeholder="Best with hot rice or roti"
                  className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Storage</label>
                <input
                  value={form.details?.storage || ""}
                  onChange={(e) => setForm({ ...form, details: { ...(form.details || {}), storage: e.target.value } })}
                  placeholder="Refrigerate after opening"
                  className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            data-testid="form-cancel"
            onClick={onClose}
            className="px-5 py-2.5 rounded-sm border-2 border-[#5A2D1D]/30 hover:border-[#5A2D1D] text-[#5A2D1D] font-semibold"
          >
            Cancel
          </button>
          <button
            data-testid="form-save"
            onClick={onSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-sm bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] font-semibold inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, required, testid, type = "text" }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="mt-1 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
    />
  </div>
);

// ---------------- Orders tab ----------------
// Modal to collect courier/tracking info before confirming dispatch
const TrackingModal = ({ order, onConfirm, onCancel }) => {
  const [courier, setCourier] = useState({ name: "", trackingId: "", trackingUrl: "" });
  const [saving,  setSaving]  = useState(false);
  const valid = courier.name.trim() && courier.trackingId.trim();

  const confirm = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await updateOrderCourier(order.id, courier);
      await updateOrderStatus(order.id, "dispatched", "Order dispatched via " + courier.name);
      track("order_status_updated_by_admin", { order_id: order.id, previous_status: order.status, new_status: "dispatched", courier_name: courier.name });
      toast.success(`${orderNumber(order)} dispatched — ${courier.name} ${courier.trackingId}`);
      onConfirm();
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#2E1A12]/60 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="w-full max-w-md bg-[#F5E6CC] rounded-sm shadow-2xl border border-[#5A2D1D]/25"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#5A2D1D]/15">
          <div>
            <h3 className="font-display text-lg font-bold text-[#5A2D1D]">Enter Tracking Details</h3>
            <p className="text-xs text-[#2E1A12]/50 mt-0.5">
              Required before marking order {orderNumber(order)} as Dispatched
            </p>
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/50 hover:text-[#5A2D1D] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
              Courier Name <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              value={courier.name}
              onChange={e => setCourier(c => ({ ...c, name: e.target.value }))}
              placeholder="e.g. DTDC, Blue Dart, Delhivery"
              className="w-full px-3 py-2.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
              Tracking ID <span className="text-red-500">*</span>
            </label>
            <input
              value={courier.trackingId}
              onChange={e => setCourier(c => ({ ...c, trackingId: e.target.value }))}
              placeholder="e.g. DTDC123456789"
              className="w-full px-3 py-2.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
              Tracking URL <span className="text-[#2E1A12]/30">(optional)</span>
            </label>
            <input
              value={courier.trackingUrl}
              onChange={e => setCourier(c => ({ ...c, trackingUrl: e.target.value }))}
              placeholder="https://track.dtdc.com/..."
              className="w-full px-3 py-2.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#5A2D1D]/15 flex items-center justify-between gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold text-[#5A2D1D] border border-[#5A2D1D]/30 rounded-sm hover:bg-[#5A2D1D]/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!valid || saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#556B2F] hover:bg-[#6a8438] disabled:bg-[#556B2F]/40 text-white text-sm font-bold rounded-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "📍"}
            Confirm Dispatch
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal to collect a mandatory reason before cancelling an order
const CancelOrderModal = ({ order, onConfirm, onCancel }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length > 0;

  const confirm = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await updateOrderStatus(order.id, "cancelled", reason.trim());
      track("order_status_updated_by_admin", { order_id: order.id, previous_status: order.status, new_status: "cancelled", reason: reason.trim(), order_total: order.total });
      toast.success(`${orderNumber(order)} cancelled`);
      onConfirm();
    } catch {
      toast.error("Failed to cancel order");
      setSaving(false);
      return;
    }
    try {
      await sendOrderMessage(order.id, {
        from: "admin",
        sender: "Annapurna Team",
        text: `Your order has been cancelled. Reason: ${reason.trim()}`,
      });
    } catch (e) {
      console.error("Cancel notification message failed:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#2E1A12]/60 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="w-full max-w-md bg-[#F5E6CC] rounded-sm shadow-2xl border border-[#5A2D1D]/25"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#5A2D1D]/15">
          <div>
            <h3 className="font-display text-lg font-bold text-red-700">Cancel Order</h3>
            <p className="text-xs text-[#2E1A12]/50 mt-0.5">
              {orderNumber(order)} — this will email the customer the reason below
            </p>
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/50 hover:text-[#5A2D1D] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
            Reason for cancellation <span className="text-red-500">*</span>
          </label>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Out of stock, customer requested, unable to deliver to this address…"
            rows={3}
            className="w-full px-3 py-2.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] resize-none"
          />
        </div>

        <div className="px-6 py-4 border-t border-[#5A2D1D]/15 flex items-center justify-between gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold text-[#5A2D1D] border border-[#5A2D1D]/30 rounded-sm hover:bg-[#5A2D1D]/5 transition-colors"
          >
            Dismiss
          </button>
          <button
            onClick={confirm}
            disabled={!valid || saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/40 text-white text-sm font-bold rounded-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
};

const FulfillmentBadge = ({ order }) => {
  const m = fulfillmentMeta(order);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${m.badgeClass}`}>
      {m.icon} {m.label}
    </span>
  );
};

// Inline status cell — dropdown + save; courier dispatched requires tracking modal
const OrderStatusCell = ({ order }) => {
  const [status,       setStatus]       = useState(order.status);
  const [saving,       setSaving]       = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const changed = status !== order.status;
  const courierDispatch = requiresCourierOnDispatch(order);
  const statusOptions = ORDER_STATUSES.filter((s) => {
    if (!courierDispatch && s.key === "dispatched") return false;
    return true;
  });

  const handleSave = (e) => {
    e.stopPropagation();
    if (status === "dispatched" && courierDispatch) {
      setShowTracking(true);
      return;
    }
    saveStatus();
  };

  const saveStatus = async () => {
    setSaving(true);
    try {
      await updateOrderStatus(order.id, status, "");
      track("order_status_updated_by_admin", { order_id: order.id, previous_status: order.status, new_status: status, order_total: order.total });
      const s = findStatus(status);
      toast.success(`${orderNumber(order)} → ${adminStatusLabel(status, s?.label, order)}`);
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  if (order.status === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border bg-red-50 text-red-700 border-red-200">
        ❌ Cancelled
      </span>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12] max-w-[140px]"
        >
          {statusOptions.map(s => (
            <option key={s.key} value={s.key}>{s.icon} {adminStatusLabel(s.key, s.label, order)}</option>
          ))}
        </select>
        {changed && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-shrink-0 px-2 py-1.5 bg-[#556B2F] hover:bg-[#6a8438] text-white text-xs font-bold rounded-sm transition-colors"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
          </button>
        )}
      </div>

      {showTracking && (
        <TrackingModal
          order={order}
          onConfirm={() => { setShowTracking(false); setStatus("dispatched"); }}
          onCancel={() => { setShowTracking(false); setStatus(order.status); }}
        />
      )}
    </>
  );
};

const PAGE_SIZE = 25;

const ORDERS_SUB_TABS = [
  { key: "all", label: "All", icon: null },
  ...ORDER_STATUSES.map(s => ({ key: s.key, label: adminStatusLabel(s.key, s.label), icon: s.icon })),
  { key: "cancelled", label: "Cancelled", icon: "❌" },
];

// Builds the full analytics payload for a "Viewed Order Details" event.
// Kept in one place so both the row-click and View-button paths stay in sync.
// Omits PII (phone, email, full address) — city/state are enough for geo insight.
function orderViewProps(o) {
  const itemCount = (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0);
  const daysSinceOrder = o.createdAt?.seconds
    ? Math.floor((Date.now() / 1000 - o.createdAt.seconds) / 86400)
    : null;
  return {
    order_id:             o.id,
    order_number:         o.order_number || null,
    status:               o.status,
    order_total:          o.total || 0,
    subtotal:             o.subtotal || 0,
    has_offer_code:       Boolean(o.offer_code),
    offer_code:           o.offer_code || null,
    discount_amount:      o.discount_amount || 0,
    gst_percent:          o.gst_percent || 0,
    gst_amount:           o.gst_amount || 0,
    delivery_charge:      o.delivery_charge || 0,
    item_count:           itemCount,
    unique_product_count: (o.items || []).length,
    product_names:        (o.items || []).map(i => i.name).join(", "),
    fulfillment_type:     o.fulfillment_type || "courier",
    order_source:         o.order_source || "website",
    payment_method:       o.payment_method || null,
    payment_confirmed:    Boolean(o.paymentConfirmed),
    has_utr:              Boolean(o.utr),
    has_courier_info:     Boolean(o.courier?.name || o.courier?.trackingId),
    delivery_city:        o.city || null,
    delivery_state:       o.state || null,
    days_since_order:     daysSinceOrder,
  };
}

const ORDERS_SOURCE_TABS = [
  { key: "all",    label: "All" },
  { key: "online", label: "Online Orders" },
  { key: "store",  label: "In Store" },
];

const OrdersAdmin = ({ orders, subTab, setSubTab, sourceTab, setSourceTab }) => {
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);

  const bySource = useMemo(
    () => (orders || []).filter(o =>
      sourceTab === "all" ||
      (sourceTab === "store" ? o.order_source === "store" : o.order_source !== "store")
    ),
    [orders, sourceTab]
  );

  const bySubTab = useMemo(
    () => bySource.filter(o => subTab === "all" || o.status === subTab),
    [bySource, subTab]
  );

  const filtered = useMemo(() => bySubTab.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.id.toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.phone || "").includes(q) ||
      (o.email || "").toLowerCase().includes(q)
    );
  }), [bySubTab, search]);

  // No `query` property here, unlike the customer-facing product search —
  // this search box is typed against customer name/phone/email, so logging
  // the literal text would leak PII into Mixpanel for no analytical benefit.
  useEffect(() => {
    if (!search.trim()) return;
    const id = setTimeout(() => {
      track("Searched Orders", {
        query_length: search.trim().length,
        result_count: filtered.length,
        zero_results: filtered.length === 0,
        sub_tab: subTab,
      });
    }, 800);
    return () => clearTimeout(id);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageOrders = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Counted within the currently-selected source tab, so switching between
  // All / Online / In Store shows status pills (and counts) scoped to that
  // slice, not the whole orders list.
  const subTabCounts = useMemo(() => ORDERS_SUB_TABS.reduce((acc, st) => {
    acc[st.key] = st.key === "all" ? bySource.length : bySource.filter(o => o.status === st.key).length;
    return acc;
  }, {}), [bySource]);

  const sourceTabCounts = useMemo(() => ORDERS_SOURCE_TABS.reduce((acc, st) => {
    acc[st.key] = st.key === "all"
      ? (orders || []).length
      : (orders || []).filter(o => st.key === "store" ? o.order_source === "store" : o.order_source !== "store").length;
    return acc;
  }, {}), [orders]);

  const goToPage = (p, method) => {
    setPage(p);
    track("Changed Orders Page", { page: p, method });
  };

  return (
    <div data-testid="admin-orders-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold text-[#5A2D1D]">
          Orders
          {orders && (
            <span className="ml-2 text-sm font-normal text-[#2E1A12]/50">
              {filtered.length}{orders.length !== filtered.length ? ` of ${orders.length}` : ""} orders
            </span>
          )}
        </h2>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5A2D1D]/50" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, phone, order #…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none"
          />
        </div>
      </div>

      {/* Source tabs — All / Online / In Store */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="orders-source-tabs">
        {ORDERS_SOURCE_TABS.map(st => (
          <button
            key={st.key}
            data-testid={`orders-source-tab-${st.key}`}
            onClick={() => {
              setSourceTab(st.key);
              // Whichever status sub-tab was selected under the previous
              // source tab may not exist (or be empty) under this one — e.g.
              // "Dispatched" almost never applies to "In Store" sales, which
              // land straight at "Delivered" — so reset to "All" statuses.
              // Done here (not in an effect keyed on sourceTab) so it only
              // fires on this deliberate click, never on a programmatic
              // sourceTab change like the new-order celebration's jump,
              // which sets its own specific status sub-tab in the same click.
              setSubTab("all");
              setPage(1);
              track("Changed Orders Source Filter", { source: st.key, order_count: sourceTabCounts[st.key] });
            }}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-sm border-2 transition-colors ${
              sourceTab === st.key
                ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
            }`}
          >
            {st.label}
            <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold grid place-items-center leading-none ${
              sourceTab === st.key ? "bg-[#F5E6CC] text-[#5A2D1D]" : "bg-[#5A2D1D]/10 text-[#5A2D1D]"
            }`}>
              {sourceTabCounts[st.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Status sub-tabs — scoped to the selected source tab above */}
      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="orders-subtabs">
        {ORDERS_SUB_TABS.filter(st => st.key === "all" || st.key === subTab || subTabCounts[st.key] > 0).map(st => (
          <button
            key={st.key}
            data-testid={`orders-subtab-${st.key}`}
            onClick={() => { setSubTab(st.key); setPage(1); track("Changed Orders Status Filter", { status: st.key, order_count: subTabCounts[st.key] }); }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
              subTab === st.key
                ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
            }`}
          >
            {st.icon && <span>{st.icon}</span>} {st.label}
            <span className={`min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold grid place-items-center leading-none ${
              subTab === st.key ? "bg-[#F5E6CC] text-[#5A2D1D]" : "bg-[#5A2D1D]/10 text-[#5A2D1D]"
            }`}>
              {subTabCounts[st.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-[#2E1A12]/70 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-8 text-center">
          {(() => {
            if (search) return "No orders match your search.";
            const sourceLabel = sourceTab === "all" ? "" : sourceTab === "store" ? "in-store " : "online ";
            if (subTab === "all") return `No ${sourceLabel}orders yet.`;
            return `No ${sourceLabel}${ORDERS_SUB_TABS.find(s => s.key === subTab)?.label.toLowerCase()} orders.`;
          })()}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-sm border border-[#5A2D1D]/20">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-[#5A2D1D] text-[#F5E6CC]">
                  {["Order #", "Date", "Customer", "Items", "Status", "Total", "Details"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageOrders.map((o, idx) => {
                  const itemNames  = (o.items || []).map(i => i.name).join(", ");
                  const shortItems = itemNames.length > 30 ? itemNames.slice(0,30) + "…" : itemNames;
                  return (
                    <tr
                      key={o.id}
                      onClick={() => { setSelected(o); track("Viewed Order Details", { ...orderViewProps(o), source: "table_row" }); }}
                      className={`border-b border-[#5A2D1D]/10 cursor-pointer transition-colors hover:bg-[#EFD9B4]/60 ${idx % 2 === 0 ? "bg-[#F5E6CC]" : "bg-[#FAF0DC]"}`}
                    >
                      <td className="px-4 py-3 font-bold text-[#5A2D1D] whitespace-nowrap">
                        <div>{orderNumber(o)}</div>
                        <FulfillmentBadge order={o} />
                      </td>
                      <td className="px-4 py-3 text-[#2E1A12]/60 whitespace-nowrap">
                        {o.createdAt?.seconds
                          ? new Date(o.createdAt.seconds * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })
                          : "—"}
                      </td>
                      <td className="px-4 py-3" data-mp-block="">
                        <div className="font-semibold text-[#2E1A12] leading-tight">{o.customer_name || "—"}</div>
                        <div className="text-[11px] text-[#2E1A12]/50">{o.phone}</div>
                      </td>
                      <td className="px-4 py-3 text-[#2E1A12]/70 max-w-[160px]">
                        <div className="truncate" title={itemNames}>{shortItems || "—"}</div>
                        {(o.items?.length || 0) > 1 && (
                          <div className="text-[11px] text-[#2E1A12]/40">{o.items.length} items</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <OrderStatusCell order={o} key={o.id + o.status} />
                      </td>
                      <td className="px-4 py-3 font-bold text-[#5A2D1D] whitespace-nowrap">
                        {formatPrice(o.total || 0)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={e => { e.stopPropagation(); setSelected(o); track("Viewed Order Details", { ...orderViewProps(o), source: "view_button" }); }}
                          className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F] hover:text-[#5A2D1D] border border-[#556B2F]/40 hover:border-[#5A2D1D]/60 px-2.5 py-1.5 rounded-sm transition-colors whitespace-nowrap"
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#EFD9B4]/60 border-t-2 border-[#5A2D1D]/20">
                  <td colSpan={6} className="px-4 py-2.5 text-xs text-[#2E1A12]/50">
                    Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} orders
                  </td>
                  <td colSpan={2} className="px-4 py-2.5 font-display font-bold text-[#5A2D1D] text-right text-sm">
                    Page {safePage} of {totalPages}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => goToPage(1, "first")}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 text-xs font-bold rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5 transition-colors"
                >
                  « First
                </button>
                <button
                  onClick={() => goToPage(Math.max(1, safePage - 1), "prev")}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 text-xs font-bold rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5 transition-colors"
                >
                  ‹ Prev
                </button>

                {/* Page number pills */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i-1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span key={`dots-${i}`} className="px-2 text-[#2E1A12]/40 text-xs">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p, "page_number")}
                        className={`w-8 h-8 text-xs font-bold rounded-sm border transition-colors ${
                          p === safePage
                            ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                            : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  onClick={() => goToPage(Math.min(totalPages, safePage + 1), "next")}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 text-xs font-bold rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5 transition-colors"
                >
                  Next ›
                </button>
                <button
                  onClick={() => goToPage(totalPages, "last")}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 text-xs font-bold rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5 transition-colors"
                >
                  Last »
                </button>
              </div>
              <div className="text-xs text-[#2E1A12]/50">
                {PAGE_SIZE} orders per page
              </div>
            </div>
          )}
        </>
      )}

      {/* Order detail — right-side drawer */}
      {selected && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-[#2E1A12]/35"
            onClick={() => setSelected(null)}
          />
          {/* Drawer */}
          <div className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[500px] bg-[#F5E6CC] border-l border-[#5A2D1D]/20 shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#5A2D1D]/15 flex-shrink-0">
              <div>
                <span className="font-display text-base font-bold text-[#5A2D1D]">
                  Order {orderNumber(selected)}
                </span>
                <span className="ml-2 text-xs text-[#2E1A12]/40">
                  {selected.createdAt?.seconds
                    ? new Date(selected.createdAt.seconds * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })
                    : ""}
                </span>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/60 hover:text-[#5A2D1D] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Drawer body — only this scrolls */}
            <div className="flex-1 overflow-y-auto p-5">
              <OrderDetailAdmin order={selected} key={selected.id} onClose={() => setSelected(null)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const OrderDetailAdmin = ({ order, onClose }) => {
  const [status, setStatus] = useState(order.status || "order_created");
  const [note, setNote] = useState("");
  const [courier, setCourier] = useState(order.courier || { name: "", trackingId: "", trackingUrl: "" });
  const [saving, setSaving] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const messages = useOrderMessages(order.id);
  const msgContainerRef = useRef(null);
  const canCancel = order.status !== "cancelled" && order.status !== "delivered";

  // Scroll only the message thread container, not the page
  useEffect(() => {
    if (msgContainerRef.current) {
      msgContainerRef.current.scrollTop = msgContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const doSave = async () => {
    setSaving(true);
    try {
      if (status !== order.status) {
        await updateOrderStatus(order.id, status, note);
        track("order_status_updated_by_admin", { order_id: order.id, previous_status: order.status, new_status: status, has_note: !!note.trim(), order_total: order.total });
        if (note.trim()) {
          await sendOrderMessage(order.id, {
            from: "admin",
            sender: "Annapurna Team",
            text: note.trim(),
          });
        }
      }
      if (courier.name || courier.trackingId) {
        await updateOrderCourier(order.id, courier);
      }
      toast.success("Order updated");
      setNote("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (status === "dispatched" && status !== order.status && requiresCourierOnDispatch(order)) {
      setShowTracking(true);
      return;
    }
    await doSave();
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setMsgSending(true);
    try {
      await sendOrderMessage(order.id, {
        from: "admin",
        sender: "Annapurna Team",
        text: msgText.trim(),
      });
      // message_length not the raw text — this is direct correspondence
      // with a customer, same reasoning as has_feedback_text elsewhere.
      track("Sent Order Message", { order_id: order.id, message_length: msgText.trim().length, sender: "admin" });
      setMsgText("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMsgSending(false);
    }
  };

  return (
    <div data-testid="admin-order-detail">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-2xl font-bold text-[#5A2D1D]">
            {orderNumber(order)}
          </div>
          <div className="text-xs text-[#556B2F] uppercase tracking-widest font-bold">
            {order.createdAt?.seconds
              ? new Date(order.createdAt.seconds * 1000).toLocaleString()
              : ""}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(order.status === "quality_check" || order.status === "ready_dispatch") && (
            <button
              onClick={() => { printOrderLabel(order); track("Printed Order Label", { order_id: order.id, status: order.status }); }}
              className="inline-flex items-center gap-1.5 bg-[#556B2F] hover:bg-[#6a8438] text-white text-sm font-semibold px-4 py-2 rounded-sm"
              title="Print shipping label"
            >
              <Printer className="w-4 h-4" />
              Print Label
            </button>
          )}
          {canCancel && (
            <button
              data-testid="admin-cancel-order"
              onClick={() => { setShowCancel(true); track("Opened Cancel Order Modal", { order_id: order.id }); }}
              className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded-sm transition-colors"
              title="Cancel this order"
            >
              <Ban className="w-4 h-4" />
              Cancel Order
            </button>
          )}
          <a
            href={`/order/${order.id}`}
            target="_blank"
            rel="noreferrer"
            onClick={() => track("Opened Customer Order View", { order_id: order.id })}
            className="text-[#556B2F] font-bold hover:underline text-sm"
          >
            Customer view →
          </a>
        </div>
      </div>

      {order.status === "cancelled" && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-800">
          <span className="font-bold">❌ Order Cancelled</span>
          {(() => {
            const ev = order.statusHistory?.slice().reverse().find((h) => h.status === "cancelled");
            return ev?.note ? <span> — {ev.note}</span> : null;
          })()}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 text-sm" data-mp-block="">
        <div className="bg-[#EFD9B4]/50 p-3 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Customer</div>
          <div className="font-bold text-[#5A2D1D]">{order.customer_name}</div>
          <div>{order.phone}</div>
          <div>{order.email}</div>
        </div>
        <div className="bg-[#EFD9B4]/50 p-3 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Address</div>
          <div className="whitespace-pre-wrap">{order.address}</div>
          {order.notes && <div className="mt-1 italic">Notes: {order.notes}</div>}
        </div>
      </div>

      {order.utr && (
        <div className="mt-3 bg-[#EFD9B4]/50 p-3 rounded-sm text-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">UTR / Payment Reference</div>
          <div className="font-bold text-[#5A2D1D]">{order.utr}</div>
        </div>
      )}

      {order.phonepe_transaction_id && (
        <div className="mt-3 bg-[#EFD9B4]/50 p-3 rounded-sm text-sm">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">PhonePe Transaction ID</div>
          <div className="font-bold text-[#5A2D1D]">{order.phonepe_transaction_id}</div>
        </div>
      )}

      <h4 className="mt-5 font-display text-lg font-bold text-[#5A2D1D]">Items</h4>
      <ul className="divide-y divide-[#5A2D1D]/10 text-sm">
        {order.items?.map((it, i) => (
          <li key={i} className="py-2">
            <div className="flex justify-between">
              <span>{it.name} — {it.size} × {it.quantity}</span>
              <span className="font-bold">{formatPrice(it.price * it.quantity)}</span>
            </div>
            {it.item_type === "combo" && Array.isArray(it.selections) && (
              <ul className="ml-4 mt-1 text-xs text-[#2E1A12]/60 list-disc">
                {it.selections.map((s, si) => (
                  <li key={si}>{s.product_name} — {s.size_label}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3 text-sm space-y-1 bg-[#EFD9B4]/40 p-3 rounded-sm">
        <div className="flex justify-between text-[#2E1A12]/80">
          <span>Subtotal</span><span>{formatPrice(order.subtotal || 0)}</span>
        </div>
        {order.offer_code && (
          <div className="flex justify-between text-[#556B2F] font-semibold">
            <span>Discount ({order.offer_code})</span><span>-{formatPrice(order.discount_amount || 0)}</span>
          </div>
        )}
        <div className="flex justify-between text-[#2E1A12]/80">
          <span>GST ({order.gst_percent}%)</span><span>{formatPrice(order.gst_amount || 0)}</span>
        </div>
        <div className="flex justify-between text-[#2E1A12]/80">
          <span>Delivery</span><span>{order.delivery_charge === 0 ? "Free" : formatPrice(order.delivery_charge || 0)}</span>
        </div>
        <div className="flex justify-between font-display font-bold text-[#5A2D1D] border-t border-[#5A2D1D]/20 pt-1">
          <span>Grand Total</span><span>{formatPrice(order.total || 0)}</span>
        </div>
      </div>

      {order.payment_method && (
        <div className="mb-3 bg-[#556B2F]/10 border border-[#556B2F]/25 rounded-sm p-3 text-sm">
          <span className="font-bold text-[#556B2F]">Payment: </span>
          {PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}
          {isStoreOrder(order) && <span className="text-[#2E1A12]/60"> · Walk-in home order</span>}
        </div>
      )}

      <div className="mb-3"><FulfillmentBadge order={order} /></div>

      {order.status !== "cancelled" && !isStoreOrder(order) && (
      <div className="mt-6 border-t border-[#5A2D1D]/15 pt-5">
        <h4 className="font-display text-lg font-bold text-[#5A2D1D] mb-3">Update Status</h4>
        <select
          data-testid="admin-status-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
        >
          {ORDER_STATUSES.filter((s) => requiresCourierOnDispatch(order) || s.key !== "dispatched").map((s) => (
            <option key={s.key} value={s.key}>
              {s.icon} {adminStatusLabel(s.key, s.label, order)}
            </option>
          ))}
        </select>
        <input
          data-testid="admin-status-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for this status change"
          className="mt-3 w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
        />

        <h4 className="mt-5 font-display text-lg font-bold text-[#5A2D1D] mb-3">
          {requiresCourierOnDispatch(order) ? "Courier Info" : "Shipping"}
        </h4>
        {requiresCourierOnDispatch(order) ? (
        <div className="grid sm:grid-cols-3 gap-2">
          <input
            data-testid="admin-courier-name"
            value={courier.name}
            onChange={(e) => setCourier({ ...courier, name: e.target.value })}
            placeholder="Courier (e.g. DTDC)"
            className="px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
          <input
            data-testid="admin-courier-tracking-id"
            value={courier.trackingId}
            onChange={(e) => setCourier({ ...courier, trackingId: e.target.value })}
            placeholder="Tracking ID"
            className="px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
          <input
            data-testid="admin-courier-url"
            value={courier.trackingUrl}
            onChange={(e) => setCourier({ ...courier, trackingUrl: e.target.value })}
            placeholder="Tracking URL"
            className="px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
        </div>
        ) : (
          <p className="text-sm text-[#2E1A12]/60">No courier — customer will collect when ready.</p>
        )}

        <button
          data-testid="admin-save-order"
          onClick={save}
          disabled={saving}
          className="mt-4 inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Update
        </button>
        <p className="text-[10px] text-[#2E1A12]/50 mt-1">Note will be visible to the customer in their order timeline.</p>
      </div>
      )}

      {/* Messages Thread */}
      <div className="mt-6 border-t border-[#5A2D1D]/15 pt-5">
        <h4 className="font-display text-lg font-bold text-[#5A2D1D] mb-3 flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> Customer Messages
          {messages.length > 0 && (
            <span className="text-xs font-normal text-[#2E1A12]/50">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
          )}
        </h4>

        {/* Thread — ref scrolls only this container */}
        <div ref={msgContainerRef} className="bg-[#EFD9B4]/30 border border-[#5A2D1D]/15 rounded-sm p-3 space-y-3 max-h-64 overflow-y-auto mb-3">
          {messages.length === 0 ? (
            <p className="text-sm text-[#2E1A12]/50 text-center py-4">No messages yet.</p>
          ) : (
            messages.map((m) => {
              const isAdmin = m.from === "admin";
              return (
                <div key={m.id} className={`flex gap-2 ${isAdmin ? "flex-row-reverse" : ""}`}>
                  <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold flex-shrink-0 ${
                    isAdmin ? "bg-[#5A2D1D] text-[#F5E6CC]" : "bg-[#556B2F] text-[#F5E6CC]"
                  }`}>
                    {isAdmin ? "A" : "C"}
                  </div>
                  <div className={`max-w-[80%] flex flex-col gap-0.5 ${isAdmin ? "items-end" : ""}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-wide ${isAdmin ? "text-[#5A2D1D]" : "text-[#556B2F]"}`}>
                      {isAdmin ? "You (Admin)" : m.sender}
                    </div>
                    <div className={`text-sm px-3 py-2 rounded-sm leading-relaxed ${
                      isAdmin
                        ? "bg-[#5A2D1D] text-[#F5E6CC] rounded-tr-none"
                        : "bg-white/80 text-[#2E1A12] rounded-tl-none border border-[#5A2D1D]/10"
                    }`}>
                      {m.text}
                    </div>
                    {m.at?.seconds && (
                      <div className="text-[10px] text-[#2E1A12]/40">
                        {new Date(m.at.seconds * 1000).toLocaleString("en-IN", {
                          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Admin reply box */}
        <div className="flex gap-2">
          <textarea
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
            placeholder="Send a message to the customer… (Enter to send)"
            rows={2}
            className="flex-1 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm resize-none"
          />
          <button
            onClick={sendMsg}
            disabled={msgSending || !msgText.trim()}
            className="self-end px-3 py-2.5 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 text-[#F5E6CC] rounded-sm transition-colors"
          >
            {msgSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {showCancel && (
        <CancelOrderModal
          order={order}
          onConfirm={() => { setShowCancel(false); onClose?.(); }}
          onCancel={() => setShowCancel(false)}
        />
      )}

      {showTracking && (
        <TrackingModal
          order={order}
          onConfirm={() => { setShowTracking(false); setStatus("dispatched"); }}
          onCancel={() => { setShowTracking(false); setStatus(order.status); }}
        />
      )}
    </div>
  );
};

// ---------------- Messages tab ----------------
const MessagesAdmin = () => {
  const list = useContactMessages();
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="admin-messages-tab">
      <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-4">Contact Messages</h2>
      {list === null ? (
        <div className="text-[#5A2D1D]/70">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[#2E1A12]/70 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          No messages yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {list.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`admin-message-${m.id}`}
              onClick={() => setSelected(m)}
              className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-3 text-left hover:border-[#5A2D1D]/50 hover:shadow-md transition-shadow"
              data-mp-block=""
            >
              <div className="font-display font-bold text-[#5A2D1D] text-sm truncate">{m.name}</div>
              <div className="text-[10px] text-[#556B2F] uppercase tracking-widest font-bold mt-1">
                {m.createdAt?.seconds
                  ? new Date(m.createdAt.seconds * 1000).toLocaleString()
                  : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <ContactMessageModal message={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const ContactMessageModal = ({ message, onClose }) => {
  const { isSuperAdmin } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm("Delete this message permanently? This can't be undone.")) return;
    setDeleting(true);
    try {
      await deleteContactMessage(message.id);
      toast.success("Message deleted");
      onClose();
    } catch (err) {
      toast.error("Could not delete. " + (err?.message || ""));
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-mp-block="">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold text-[#5A2D1D]">{message.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5A2D1D]/60 hover:text-[#5A2D1D]" /></button>
        </div>
        <div className="text-xs text-[#556B2F] uppercase tracking-widest font-bold mb-4">
          {message.createdAt?.seconds ? new Date(message.createdAt.seconds * 1000).toLocaleString() : ""}
        </div>
        <div className="text-sm text-[#2E1A12]/70">
          {message.email} {message.phone && `· ${message.phone}`}
        </div>
        <div className="mt-3 text-sm whitespace-pre-wrap">{message.message}</div>

        {isSuperAdmin && (
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-red-700 hover:text-red-900 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Customers tab ----------------
const customerOrders = (c, orders) =>
  orders.filter((o) =>
    o.uid === c.id || (!o.uid && o.email && c.email && o.email.toLowerCase() === c.email.toLowerCase())
  );

const CustomerEditModal = ({ customer, onClose }) => {
  const [form, setForm] = useState({ name: customer.name || "", phone: customer.phone || "", dob: customer.dob || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateCustomer(customer.id, form);
      track("customer_updated_by_admin", { customer_id: customer.id });
      toast.success("Customer updated");
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-[#5A2D1D]">Edit Customer</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5A2D1D]/60 hover:text-[#5A2D1D]" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Phone</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold">Date of Birth</label>
            <input
              type="date"
              value={form.dob}
              onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
          </div>
          <p className="text-xs text-[#2E1A12]/50">Email is tied to their login and can't be changed here.</p>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-sm text-sm font-semibold text-[#5A2D1D] hover:bg-[#5A2D1D]/5">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomerDetailModal = ({ customer, orders, onClose }) => {
  const mine = customerOrders(customer, orders);
  const revenue = mine.filter((o) => PAID_STATUSES.includes(o.status)).reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-mp-block="">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl font-bold text-[#5A2D1D]">{customer.name || customer.email}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5A2D1D]/60 hover:text-[#5A2D1D]" /></button>
        </div>
        <div className="text-sm text-[#2E1A12]/70 mb-5">{customer.email}</div>

        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">Orders Placed</div>
            <div className="text-xl font-bold text-[#5A2D1D] flex items-center gap-1.5"><ShoppingCart className="w-4 h-4" />{mine.length}</div>
          </div>
          <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">Revenue Generated</div>
            <div className="text-xl font-bold text-[#5A2D1D] flex items-center gap-1.5"><TrendingUp className="w-4 h-4" />{formatPrice(revenue)}</div>
          </div>
          <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">Customer Since</div>
            <div className="text-xl font-bold text-[#5A2D1D] flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {customer.createdAt?.seconds ? new Date(customer.createdAt.seconds * 1000).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">Phone</div>
            <div className="text-[#2E1A12]">{customer.phone || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">Date of Birth</div>
            <div className="text-[#2E1A12]">{customer.dob || "—"}</div>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-2">Delivery Addresses</div>
          {(customer.addresses || []).length === 0 ? (
            <div className="text-sm text-[#2E1A12]/50">No saved addresses.</div>
          ) : (
            <div className="space-y-2">
              {customer.addresses.map((a, i) => (
                <div key={a.id || i} className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-3 text-sm">
                  {a.isDefault && <span className="text-[10px] font-bold text-[#556B2F] uppercase tracking-widest mr-2">Default</span>}
                  {[a.address, a.city, a.state, a.pincode, a.country].filter(Boolean).join(", ")}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-2">Order History</div>
          {mine.length === 0 ? (
            <div className="text-sm text-[#2E1A12]/50">No orders yet.</div>
          ) : (
            <ul className="divide-y divide-[#5A2D1D]/10">
              {mine.map((o) => {
                const status = findStatus(o.status);
                return (
                  <li key={o.id} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold text-[#5A2D1D]">{orderNumber(o)}</div>
                      <div className="text-xs text-[#2E1A12]/60">
                        {o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000).toLocaleDateString() : "—"} · {adminStatusLabel(o.status, status?.label, o) || o.status}
                      </div>
                    </div>
                    <div className="font-bold text-[#5A2D1D]">{formatPrice(o.total || 0)}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const POLICY_SUB_TABS = [
  { key: "terms",        label: "Terms & Conditions" },
  { key: "privacy",      label: "Privacy Policy" },
  { key: "shipping",     label: "Shipping Policy" },
  { key: "refund",       label: "Refund Policy" },
  { key: "return",       label: "Return Policy" },
  { key: "cancellation", label: "Cancellation Policy" },
];

const USERS_SUB_TABS = [
  { key: "customers",  label: "Customers" },
  { key: "admins",     label: "Admins" },
  { key: "associates", label: "Store Associates" },
];

const UsersAdmin = ({ orders }) => {
  const allCustomers = useAllCustomers();
  const customersList = allCustomers ? allCustomers.filter((c) => !c.is_admin && !c.is_store_associate) : null;
  const adminsList = allCustomers ? allCustomers.filter((c) => c.is_admin && !c.is_store_associate) : null;
  const associatesList = allCustomers ? allCustomers.filter((c) => c.is_store_associate) : null;
  const [subTab, setSubTab] = useState("customers");

  const counts = {
    customers: customersList?.length ?? 0,
    admins: adminsList?.length ?? 0,
    associates: associatesList?.length ?? 0,
  };

  // Fire once after allCustomers loads — component stays mounted (CSS hidden)
  // so this runs exactly once per session when the tab is first visited.
  const usersTabEventSentRef = useRef(false);
  useEffect(() => {
    if (usersTabEventSentRef.current || allCustomers === null) return;
    usersTabEventSentRef.current = true;
    track("Viewed Users Tab", {
      source:           "admin_tab_nav",
      total_users:      allCustomers.length,
      customer_count:   counts.customers,
      admin_count:      counts.admins,
      associate_count:  counts.associates,
    });
  }, [allCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-users-tab">
      <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-4">Users</h2>

      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="users-subtabs">
        {USERS_SUB_TABS.map(st => (
          <button
            key={st.key}
            data-testid={`users-subtab-${st.key}`}
            onClick={() => { setSubTab(st.key); track("Changed Users Sub-Tab", { sub_tab: st.key, count: counts[st.key] }); }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
              subTab === st.key
                ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
            }`}
          >
            {st.label}
            <span className={`min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold grid place-items-center leading-none ${
              subTab === st.key ? "bg-[#F5E6CC] text-[#5A2D1D]" : "bg-[#5A2D1D]/10 text-[#5A2D1D]"
            }`}>
              {counts[st.key]}
            </span>
          </button>
        ))}
      </div>

      {subTab === "customers" ? (
        <CustomersAdmin list={customersList} orders={orders} />
      ) : subTab === "admins" ? (
        <AdminsAdmin list={adminsList} orders={orders} />
      ) : (
        <StoreAssociatesAdmin list={associatesList} orders={orders} />
      )}
    </div>
  );
};

const StoreAssociatesAdmin = ({ list, orders }) => {
  const BLANK_FORM = { name: "", store_user_id: "", password: "" };
  const [form, setForm] = useState(BLANK_FORM);
  const [formKey, setFormKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editing, setEditing] = useState(null);

  const resetCreateForm = () => {
    setForm(BLANK_FORM);
    setShowPassword(false);
    setFormKey((k) => k + 1);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    const nameErr = !form.name.trim() ? "Associate name is required." : null;
    const idErr = validateStoreUserId(form.store_user_id);
    const pwErr = validateStorePassword(form.password);
    if (nameErr || idErr || pwErr) {
      toast.error(nameErr || idErr || pwErr);
      return;
    }
    setCreating(true);
    try {
      await createStoreAssociate({
        name: form.name.trim(),
        store_user_id: form.store_user_id.trim(),
        password: form.password,
      });
      track("store_associate_created", { store_user_id: form.store_user_id.trim() });
      toast.success("Store associate created — share the user ID and password with your staff");
      resetCreateForm();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (c) => {
    const label = c.name || c.store_user_id || "this associate";
    if (!window.confirm(`Remove store associate ${label}? They will no longer be able to sign in.`)) return;
    try {
      await deleteCustomer(c.id);
      track("store_associate_deleted", { customer_id: c.id });
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-store-associates-section" className="mb-8 pb-8 border-b border-[#5A2D1D]/20">
      <h3 className="font-display text-xl font-bold text-[#5A2D1D] mb-1 flex items-center gap-2">
        <Store className="w-5 h-5" /> Store Associates
      </h3>
      <p className="text-sm text-[#2E1A12]/70 mb-4">
        Kitchen staff sign in with their <b>user ID</b> and <b>password</b> at{" "}
        <code className="bg-[#5A2D1D]/10 px-1 rounded-sm">/store</code> only — not on the public customer sign-in.
        If an associate cannot log in, open <b>Edit</b> and click <b>Save</b> once to refresh their login.
      </p>

      <form
        key={formKey}
        onSubmit={onCreate}
        autoComplete="off"
        className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-4 mb-6 max-w-lg space-y-3"
      >
        <div className="font-display font-bold text-[#5A2D1D] text-sm">New store associate</div>
        <input
          data-testid="associate-name-input"
          type="text"
          placeholder="Name *"
          required
          autoComplete="off"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
        />
        <input
          data-testid="associate-userid-input"
          type="text"
          inputMode="numeric"
          placeholder="User ID * (2 digits)"
          required
          maxLength={2}
          autoComplete="off"
          value={form.store_user_id}
          onChange={(e) => setForm((f) => ({ ...f, store_user_id: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
          className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
        />
        <div className="relative">
          <input
            data-testid="associate-password-input"
            type={showPassword ? "text" : "password"}
            placeholder="Password * (e.g. abc123)"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="w-full px-3 py-2 pr-10 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
          />
          <button
            type="button"
            onClick={() => { setShowPassword((v) => !v); track("Toggled Password Visibility", { now_visible: !showPassword, context: "create_store_associate" }); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button
          data-testid="associate-create-button"
          type="submit"
          disabled={creating}
          className="inline-flex items-center gap-2 bg-[#556B2F] hover:bg-[#6a8438] text-[#F5E6CC] px-4 py-2 rounded-full text-sm font-bold disabled:opacity-60"
        >
          {creating && <Loader2 className="w-4 h-4 animate-spin" />}
          Create associate
        </button>
      </form>

      {list === null ? (
        <div className="text-[#5A2D1D]/70">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[#2E1A12]/70 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          No store associates yet. Create one above.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-[#556B2F] font-bold border-b border-[#5A2D1D]/20">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">User ID</th>
                <th className="py-2 pr-3">Store sales</th>
                <th className="py-2 pr-3">Revenue</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const { salesCount, revenue } = associateStoreStats(c.id, orders);
                return (
                  <tr key={c.id} data-testid={`admin-associate-${c.id}`} className="border-b border-[#5A2D1D]/10">
                    <td className="py-2 pr-3 font-semibold text-[#5A2D1D]">{c.name || "—"}</td>
                    <td className="py-2 pr-3 font-mono font-bold">{c.store_user_id || "—"}</td>
                    <td className="py-2 pr-3 font-semibold">{salesCount}</td>
                    <td className="py-2 pr-3 font-bold text-[#5A2D1D]">{formatPrice(revenue)}</td>
                    <td className="py-2 pr-3 text-xs text-[#556B2F]">
                      {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-2">
                        <button
                          data-testid={`admin-associate-edit-${c.id}`}
                          onClick={() => { setEditing(c); track("Opened Edit Store Associate Form", { customer_id: c.id }); }}
                          className="text-[#5A2D1D]/70 hover:text-[#5A2D1D]"
                          title="Edit associate"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`admin-associate-delete-${c.id}`}
                          onClick={() => onDelete(c)}
                          className="text-[#7A3B25]/70 hover:text-[#7A3B25]"
                          title="Remove associate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <StoreAssociateEditModal associate={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const StoreAssociateEditModal = ({ associate, onClose }) => {
  const [form, setForm] = useState({
    name: associate.name || "",
    store_user_id: associate.store_user_id || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const nameErr = !form.name.trim() ? "Associate name is required." : null;
    const idErr = validateStoreUserId(form.store_user_id);
    const pwErr = validateStorePassword(form.password, { required: false });
    if (nameErr || idErr || pwErr) {
      toast.error(nameErr || idErr || pwErr);
      return;
    }
    setSaving(true);
    try {
      await updateStoreAssociate({
        id: associate.id,
        name: form.name.trim(),
        store_user_id: form.store_user_id.trim(),
        password: form.password || undefined,
      });
      track("store_associate_updated", { customer_id: associate.id, password_changed: !!form.password });
      toast.success("Store associate updated");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm p-6 max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="associate-edit-modal"
      >
        <h3 className="font-display text-xl font-bold text-[#5A2D1D] mb-4">Edit store associate</h3>
        <form onSubmit={save} className="space-y-3">
          <input
            type="text"
            placeholder="Name *"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="User ID * (2 digits)"
            required
            maxLength={2}
            value={form.store_user_id}
            onChange={(e) => setForm((f) => ({ ...f, store_user_id: e.target.value.replace(/\D/g, "").slice(0, 2) }))}
            className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New password (leave blank to keep current)"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full px-3 py-2 pr-10 rounded-sm border border-[#5A2D1D]/30 bg-white text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-2.5 rounded-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2.5 border border-[#5A2D1D]/30 rounded-sm font-semibold">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomersAdmin = ({ list, orders }) => {
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const onDelete = async (c) => {
    if (!window.confirm(`Permanently delete ${c.name || c.email}? This removes their login and they won't be able to sign in again. Their past orders are kept for records, and no email is sent to them.`)) return;
    try {
      await deleteCustomer(c.id);
      track("customer_deleted_by_admin", { customer_id: c.id });
      toast.success(`${c.name || c.email} deleted`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onMakeAdmin = async (c) => {
    if (!window.confirm(`Make ${c.name || c.email} an admin? They will get full access to this dashboard, including orders, customers, and settings.`)) return;
    try {
      await updateCustomer(c.id, { is_admin: true });
      track("customer_promoted_to_admin", { customer_id: c.id });
      toast.success(`${c.name || c.email} is now an admin`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-customers-tab">
      {list === null ? (
        <div className="text-[#5A2D1D]/70">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[#2E1A12]/70 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          No customers signed up yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-[#556B2F] font-bold border-b border-[#5A2D1D]/20">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Phone</th>
                <th className="py-2 pr-3">Joined</th>
                <th className="py-2 pr-3">Orders</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`admin-customer-${c.id}`}
                  className="border-b border-[#5A2D1D]/10 hover:bg-[#5A2D1D]/5 cursor-pointer"
                  onClick={() => { setViewing(c); track("Viewed Customer Details", { customer_id: c.id }); }}
                  data-mp-block=""
                >
                  <td className="py-2 pr-3 font-semibold text-[#5A2D1D]">{c.name || "—"}</td>
                  <td className="py-2 pr-3">{c.email}</td>
                  <td className="py-2 pr-3">{c.phone || "—"}</td>
                  <td className="py-2 pr-3 text-[#556B2F] font-semibold text-xs">
                    {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2 pr-3 font-semibold">{customerOrders(c, orders).length}</td>
                  <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <button
                        data-testid={`admin-customer-make-admin-${c.id}`}
                        onClick={() => onMakeAdmin(c)}
                        className="text-[#556B2F]/70 hover:text-[#556B2F]"
                        title="Make admin"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-customer-edit-${c.id}`}
                        onClick={() => { setEditing(c); track("Opened Edit Customer Form", { customer_id: c.id }); }}
                        className="text-[#5A2D1D]/70 hover:text-[#5A2D1D]"
                        title="Edit customer"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-customer-delete-${c.id}`}
                        onClick={() => onDelete(c)}
                        className="text-[#7A3B25]/70 hover:text-[#7A3B25]"
                        title="Delete customer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <CustomerEditModal customer={editing} onClose={() => setEditing(null)} />}
      {viewing && <CustomerDetailModal customer={viewing} orders={orders} onClose={() => setViewing(null)} />}
    </div>
  );
};

const AdminsAdmin = ({ list, orders }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const onRemoveAdmin = async (c) => {
    if (!window.confirm(`Remove admin access for ${c.name || c.email}? They'll become a regular customer and lose access to this dashboard.`)) return;
    try {
      await updateCustomer(c.id, { is_admin: false });
      track("admin_demoted_to_customer", { customer_id: c.id });
      toast.success(`${c.name || c.email} is no longer an admin`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Permanently delete admin account ${c.name || c.email}? This removes their login and they won't be able to sign in again. Their past orders are kept for records, and no email is sent to them.`)) return;
    try {
      await deleteCustomer(c.id);
      track("admin_deleted_by_admin", { customer_id: c.id });
      toast.success(`${c.name || c.email} deleted`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-admins-tab">
      <p className="text-sm text-[#2E1A12]/70 mb-4">Accounts with full access to this dashboard.</p>
      {list === null ? (
        <div className="text-[#5A2D1D]/70">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[#2E1A12]/70 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          No admin accounts found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-[#556B2F] font-bold border-b border-[#5A2D1D]/20">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Phone</th>
                <th className="py-2 pr-3">Joined</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const isSelf = c.id === user?.id;
                return (
                  <tr
                    key={c.id}
                    data-testid={`admin-admin-${c.id}`}
                    className="border-b border-[#5A2D1D]/10 hover:bg-[#5A2D1D]/5 cursor-pointer"
                    onClick={() => { setViewing(c); track("Viewed Customer Details", { customer_id: c.id, role: "admin" }); }}
                    data-mp-block=""
                  >
                    <td className="py-2 pr-3 font-semibold text-[#5A2D1D]">
                      {c.name || "—"} {isSelf && <span className="text-[10px] text-[#556B2F] font-bold uppercase tracking-widest">(You)</span>}
                    </td>
                    <td className="py-2 pr-3">{c.email}</td>
                    <td className="py-2 pr-3">{c.phone || "—"}</td>
                    <td className="py-2 pr-3 text-[#556B2F] font-semibold text-xs">
                      {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <button
                          data-testid={`admin-edit-${c.id}`}
                          onClick={() => { setEditing(c); track("Opened Edit Customer Form", { customer_id: c.id, role: "admin" }); }}
                          className="text-[#5A2D1D]/70 hover:text-[#5A2D1D]"
                          title="Edit admin"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {!isSelf && (
                          <>
                            <button
                              data-testid={`admin-remove-admin-${c.id}`}
                              onClick={() => onRemoveAdmin(c)}
                              className="text-[#5A2D1D]/70 hover:text-[#5A2D1D]"
                              title="Remove admin access"
                            >
                              <ShieldOff className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`admin-delete-${c.id}`}
                              onClick={() => onDelete(c)}
                              className="text-[#7A3B25]/70 hover:text-[#7A3B25]"
                              title="Delete admin account"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <CustomerEditModal customer={editing} onClose={() => setEditing(null)} />}
      {viewing && <CustomerDetailModal customer={viewing} orders={orders} onClose={() => setViewing(null)} />}
    </div>
  );
};

// ---------------- Settings tab ----------------
// ---------------- Site Content tab ----------------
const CONTENT_FIELDS = [
  { section: "Hero (Home page)", fields: [
    { key: "hero_headline_1", label: "Headline — line 1" },
    { key: "hero_headline_2", label: "Headline — line 2 (italic)" },
    { key: "hero_headline_3", label: "Headline — line 3 (underlined)" },
    { key: "hero_subhead",    label: "Subheading paragraph", multiline: true },
  ]},
  { section: "About page", fields: [
    { key: "about_h1",   label: "Heading — line 1" },
    { key: "about_h2",   label: "Heading — line 2 (italic)" },
    { key: "about_body", label: "Story paragraph", multiline: true },
  ]},
  { section: "Why Choose Us (About page)", fields: [
    { key: "why_h1", label: "Heading — line 1" },
    { key: "why_h2", label: "Heading — line 2 (italic)" },
  ]},
];

// Guest checkout + message cooldown aren't pricing — they live here (Site
// Content) instead of the Charges and Tax tab, which is pricing-only. Both
// still write into the same checkout settings blob; this just merges its
// two fields into the latest live settings rather than overwriting them.
const CheckoutBehaviorSettings = () => {
  const current = useCheckoutSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // See the matching comment in SettingsAdmin — must wait for the real
    // fetch, not the synchronous DEFAULT_CHECKOUT_SETTINGS placeholder.
    if (!form && current !== DEFAULT_CHECKOUT_SETTINGS) {
      setForm({
        enableGuestCheckout: current.enableGuestCheckout,
        enableQrCheckout: current.enableQrCheckout,
        messageCooldownMinutes: current.messageCooldownMinutes,
        restrictDelivery: current.restrictDelivery,
        pincodesText: (current.serviceablePincodes || []).join("\n"),
      });
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      const serviceablePincodes = form.pincodesText
        .split(/[\n,]/)
        .map(s => s.trim())
        .filter(Boolean);
      await updateCheckoutSettings({
        ...current,
        enableGuestCheckout: form.enableGuestCheckout,
        enableQrCheckout: form.enableQrCheckout,
        messageCooldownMinutes: Number(form.messageCooldownMinutes) || 0,
        restrictDelivery: form.restrictDelivery,
        serviceablePincodes,
      });
      track("Saved Checkout Behavior Settings", {
        // Current values being saved
        enable_guest_checkout:      form.enableGuestCheckout,
        message_cooldown_minutes:   Number(form.messageCooldownMinutes) || 0,
        restrict_delivery:          form.restrictDelivery,
        serviceable_pincodes_count: serviceablePincodes.length,
        serviceable_pincodes:       serviceablePincodes.join(", ") || null,
        // What changed vs the pre-save DB values (before / after comparison)
        guest_checkout_changed:     form.enableGuestCheckout !== current.enableGuestCheckout,
        cooldown_changed:           (Number(form.messageCooldownMinutes) || 0) !== (current.messageCooldownMinutes || 0),
        restrict_delivery_changed:  form.restrictDelivery !== current.restrictDelivery,
        pincodes_changed:           serviceablePincodes.join(",") !== (current.serviceablePincodes || []).join(","),
        prev_enable_guest_checkout:      current.enableGuestCheckout,
        prev_message_cooldown_minutes:   current.messageCooldownMinutes || 0,
        prev_restrict_delivery:          current.restrictDelivery,
        prev_serviceable_pincodes_count: (current.serviceablePincodes || []).length,
      });
      toast.success("Checkout behavior saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-6 mb-6">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Checkout Behavior</div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-[#2E1A12]">Allow Guest Checkout</div>
          <div className="text-xs text-[#556B2F] mt-0.5">
            When off, visitors must sign in before checking out (currently they can check out without an account).
          </div>
        </div>
        <button
          type="button"
          data-testid="toggle-guest-checkout"
          onClick={() => setForm(f => ({ ...f, enableGuestCheckout: !f.enableGuestCheckout }))}
          className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.enableGuestCheckout ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}
        >
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.enableGuestCheckout ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-[#2E1A12]">UPI QR Checkout</div>
          <div className="text-xs text-[#556B2F] mt-0.5">
            When on, customers pay via UPI QR scan and submit a UTR. When off, only PhonePe online payment is shown.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setForm(f => ({ ...f, enableQrCheckout: !f.enableQrCheckout }))}
          className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.enableQrCheckout ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}
        >
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.enableQrCheckout ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      <SettingRow
        label="Message Cooldown (minutes)"
        hint="How long a customer must wait between messages on their order's chat thread"
        value={form.messageCooldownMinutes}
        onChange={v => setForm(f => ({ ...f, messageCooldownMinutes: v }))}
        suffix="min"
      />

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-[#2E1A12]">Restrict Delivery Area</div>
          <div className="text-xs text-[#556B2F] mt-0.5">
            When on, checkout is blocked for pincodes outside the list below.
          </div>
        </div>
        <button
          type="button"
          data-testid="toggle-restrict-delivery"
          onClick={() => setForm(f => ({ ...f, restrictDelivery: !f.restrictDelivery }))}
          className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.restrictDelivery ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}
        >
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.restrictDelivery ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {form.restrictDelivery && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">
            Serviceable Pincodes
          </label>
          <p className="text-xs text-[#2E1A12]/60 mt-0.5 mb-1.5">
            One per line (or comma-separated). Each can be a full 6-digit pincode or a shorter prefix —
            e.g. <span className="font-mono">533</span> matches every pincode starting with 533.
          </p>
          <textarea
            data-testid="serviceable-pincodes-input"
            value={form.pincodesText}
            onChange={(e) => setForm(f => ({ ...f, pincodesText: e.target.value }))}
            rows={4}
            placeholder={"533101\n533103\n533"}
            className="w-full max-w-sm px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm font-mono resize-none"
          />
        </div>
      )}

      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold disabled:opacity-60"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save Checkout Behavior
      </button>
    </div>
  );
};

const BannerImagesAdmin = () => {
  const site = useSiteContent();
  const [uploading, setUploading] = useState(false);
  const [deletingIdx, setDeletingIdx] = useState(null);

  const banners = Array.isArray(site.banners) ? site.banners : [];

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const optimized = await compressImage(file);
      const url = await uploadProductImage(optimized);
      const updated = [...banners, { src: url, alt: "Annapurna Home Pickles" }];
      await saveSiteContent({ ...site, banners: updated });
      track("banner_image_added", { banner_count: updated.length });
      toast.success("Banner added");
    } catch (e) {
      toast.error("Upload failed: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (idx) => {
    setDeletingIdx(idx);
    try {
      const updated = banners.filter((_, i) => i !== idx);
      await saveSiteContent({ ...site, banners: updated });
      track("banner_image_deleted", { banner_count: updated.length });
      toast.success("Banner removed");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingIdx(null);
    }
  };

  return (
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Banner Images</div>
          <div className="text-xs text-[#2E1A12]/60 mt-0.5">Shown in the hero slideshow on the homepage. Changes are live immediately.</div>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-1.5 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-4 py-2 rounded-sm text-sm font-semibold transition-colors flex-shrink-0">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add Banner
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
        </label>
      </div>

      {banners.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#2E1A12]/50 border-2 border-dashed border-[#5A2D1D]/20 rounded-sm">
          No banners added yet — the hero will show the default images.<br />
          <span className="text-xs">Upload at least one banner to replace the defaults.</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {banners.map((b, idx) => (
            <div key={b.src + idx} className="relative group aspect-[4/3] rounded-sm overflow-hidden bg-[#EFD9B4]">
              <img src={b.src} alt={b.alt} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <button
                  type="button"
                  disabled={deletingIdx === idx}
                  onClick={() => handleDelete(idx)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-sm"
                >
                  {deletingIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Delete
                </button>
              </div>
              <div className="absolute bottom-1 right-1.5 text-[10px] font-bold text-white/70 bg-black/30 px-1.5 py-0.5 rounded-sm">
                {idx + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PICKUP_ADDRESS_FIELDS = [
  { key: "name",    label: "Location Name",  placeholder: "e.g. Annapurna Home Pickles" },
  { key: "address", label: "Street Address", placeholder: "Street, area, landmark" },
  { key: "city",    label: "City",           placeholder: "e.g. Rajahmundry" },
  { key: "state",   label: "State",          placeholder: "e.g. Andhra Pradesh" },
  { key: "pincode", label: "Pincode",        placeholder: "e.g. 533101" },
  { key: "note",    label: "Customer Note",  placeholder: "e.g. Collect when we notify you — 2–5 days after order is ready" },
];

const PickupAddressSettings = () => {
  const current = useCheckoutSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form && current !== DEFAULT_CHECKOUT_SETTINGS) {
      const pa = current.pickupAddress || {};
      setForm({
        name:    pa.name    || "",
        address: pa.address || "",
        city:    pa.city    || "",
        state:   pa.state   || "",
        pincode: pa.pincode || "",
        note:    pa.note    || "",
      });
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      const prev = current.pickupAddress || {};
      const next = {
        name:    form.name.trim(),
        address: form.address.trim(),
        city:    form.city.trim(),
        state:   form.state.trim(),
        pincode: form.pincode.trim(),
        note:    form.note.trim(),
      };
      await updateCheckoutSettings({ ...current, pickupAddress: next });
      track("Saved Pickup Address", {
        name:           next.name    || null,
        city:           next.city    || null,
        state:          next.state   || null,
        pincode:        next.pincode || null,
        has_note:       !!next.note,
        name_changed:    next.name    !== (prev.name    || ""),
        address_changed: next.address !== (prev.address || ""),
        city_changed:    next.city    !== (prev.city    || ""),
        state_changed:   next.state   !== (prev.state   || ""),
        pincode_changed: next.pincode !== (prev.pincode || ""),
        note_changed:    next.note    !== (prev.note    || ""),
      });
      toast.success("Pickup address saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Pickup Address</div>
        <p className="text-xs text-[#2E1A12]/50 mt-0.5">Shown to customers at checkout when they select "Pickup Order"</p>
      </div>
      {PICKUP_ADDRESS_FIELDS.map(({ key, label, placeholder }) => (
        <div key={key}>
          <label className="text-xs font-semibold text-[#2E1A12]/70 block mb-1">{label}</label>
          <input
            value={form[key]}
            onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
            placeholder={placeholder}
            className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/25 text-sm bg-[#F5E6CC] focus:outline-none focus:border-[#5A2D1D]/60"
          />
        </div>
      ))}
      <button
        onClick={save}
        disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-2.5 rounded-sm font-bold text-sm transition-colors"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save Pickup Address
      </button>
    </div>
  );
};

const PolicyContentAdmin = () => {
  const current = usePolicyContent();
  const [subTab, setSubTab] = useState("terms");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Same fix as SiteContentAdmin — usePolicyContent() starts synchronously
    // on the DEFAULT_POLICY_CONTENT placeholder before the real fetch resolves.
    // Every field is seeded with the real current legal text (POLICY_SEED_CONTENT)
    // when there's no saved override yet, so the editor shows actual content
    // instead of a blank box — but "live" (the dot indicator, and what the
    // public page actually renders) is always judged from `current[key]`
    // itself, never from this seeded fallback, so just opening a tab can
    // never silently switch a page away from its original hardcoded text.
    if (!form && current !== DEFAULT_POLICY_CONTENT) {
      const seeded = {};
      for (const key of Object.keys(DEFAULT_POLICY_CONTENT)) {
        seeded[key] = current[key] || POLICY_SEED_CONTENT[key] || "";
      }
      setForm(seeded);
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div className="text-[#5A2D1D]/70">Loading…</div>;

  const activeLabel = POLICY_SUB_TABS.find(s => s.key === subTab)?.label;

  const save = async () => {
    setSaving(true);
    try {
      // Only the currently-open tab is written, merged over the live DB
      // state for every other key — switching tabs to look around never
      // risks pushing a different tab's seeded-but-unreviewed text live.
      const next = { ...current, [subTab]: form[subTab] };
      await savePolicyContent(next);
      track("Saved Policy Page Content", {
        source: "admin_tab_nav",
        policy: subTab,
        is_override: !!(form[subTab] || "").trim(),
      });
      toast.success(`${activeLabel} saved`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-2">Policy Pages</div>
      <p className="text-xs text-[#2E1A12]/60 mb-3">
        Each box is pre-filled with that page's current text. Edit and save to change what's shown live —
        clear a box entirely and save to revert that page to its original default text. Supports{" "}
        <code className="font-mono">## Heading</code>, <code className="font-mono">- bullet</code>,{" "}
        <code className="font-mono">1. numbered</code>, <code className="font-mono">{"> highlighted note"}</code>,{" "}
        <code className="font-mono">**bold**</code>, and <code className="font-mono">[label](url)</code> links.
        The dot on a tab means that page currently has a saved override live on the site.
      </p>

      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="policy-subtabs">
        {POLICY_SUB_TABS.map(st => (
          <button
            key={st.key}
            type="button"
            data-testid={`policy-subtab-${st.key}`}
            onClick={() => setSubTab(st.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
              subTab === st.key
                ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
            }`}
          >
            {st.label}
            {!!(current[st.key] || "").trim() && (
              <span className={`w-1.5 h-1.5 rounded-full ${subTab === st.key ? "bg-[#F5E6CC]" : "bg-[#556B2F]"}`} />
            )}
          </button>
        ))}
      </div>

      <textarea
        data-testid={`policy-field-${subTab}`}
        value={form[subTab] ?? ""}
        onChange={(e) => setForm((f) => ({ ...f, [subTab]: e.target.value }))}
        rows={16}
        className="w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm font-mono"
      />

      <button
        type="button"
        data-testid="policy-save-button"
        onClick={save}
        disabled={saving}
        className="mt-3 inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold disabled:opacity-60"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save {activeLabel}
      </button>
    </div>
  );
};

const CUSTOMER_SITE_SUB_TABS = [
  { key: "checkout", label: "Checkout" },
  { key: "banners",  label: "Banners" },
  { key: "pages",    label: "Home & About Pages" },
  { key: "legal",    label: "Legal Pages" },
];

const SiteContentAdmin = () => {
  const current = useSiteContent();
  const checkoutSettings = useCheckoutSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [siteSettingsSubTab, setSiteSettingsSubTab] = useState("customer");
  const [customerSubTab, setCustomerSubTab] = useState("checkout");

  useEffect(() => {
    // Same fix as SettingsAdmin/CheckoutBehaviorSettings — useSiteContent()
    // also starts on the synchronous DEFAULT_SITE_CONTENT placeholder.
    if (!form && current !== DEFAULT_SITE_CONTENT) setForm(current);
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire once after both site content and checkout settings have loaded from
  // Supabase — component stays mounted (CSS hidden) so this fires exactly once
  // per session when the tab is first visited.
  const contentTabEventSentRef = useRef(false);
  useEffect(() => {
    if (
      contentTabEventSentRef.current ||
      current === DEFAULT_SITE_CONTENT ||
      checkoutSettings === DEFAULT_CHECKOUT_SETTINGS
    ) return;
    contentTabEventSentRef.current = true;

    // Content sections — flag whether each section has been customised from
    // the factory defaults (exact text content is not sent — not useful for
    // analytics and would bloat every event).
    const heroCustomized    = ["hero_headline_1","hero_headline_2","hero_headline_3","hero_subhead"]
      .some(k => current[k] !== DEFAULT_SITE_CONTENT[k]);
    const aboutCustomized   = ["about_h1","about_h2","about_body"]
      .some(k => current[k] !== DEFAULT_SITE_CONTENT[k]);
    const whyCustomized     = ["why_h1","why_h2"]
      .some(k => current[k] !== DEFAULT_SITE_CONTENT[k]);

    track("Viewed Site Settings Tab", {
      source:                     "admin_tab_nav",
      // Checkout behaviour
      enable_guest_checkout:      checkoutSettings.enableGuestCheckout,
      enable_pickup:              checkoutSettings.enablePickup,
      enable_courier:             checkoutSettings.enableCourier,
      restrict_delivery:          checkoutSettings.restrictDelivery,
      message_cooldown_minutes:   checkoutSettings.messageCooldownMinutes,
      serviceable_pincodes_count: (checkoutSettings.serviceablePincodes || []).length,
      // Marketing content customisation flags
      hero_customized:            heroCustomized,
      about_customized:           aboutCustomized,
      why_customized:             whyCustomized,
      any_content_customized:     heroCustomized || aboutCustomized || whyCustomized,
    });
  }, [current, checkoutSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div className="text-[#5A2D1D]/70">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      // Always carry the live banners forward — the form only tracks text
      // fields and was initialized once; banner changes made via BannerImagesAdmin
      // would otherwise be silently overwritten when text content is saved.
      await saveSiteContent({ ...form, banners: current.banners ?? [] });
      // Compute which fields changed vs the live DB values (current) so we
      // can see the exact diff in Mixpanel without a second lookup.
      const allContentKeys = CONTENT_FIELDS.flatMap(s => s.fields.map(f => f.key));
      const changedFields  = allContentKeys.filter(k => (form[k] || "") !== (current[k] || ""));
      track("Saved Site Content", {
        // Every saved field value (actual text — requested by admin for audit trail)
        hero_headline_1: form.hero_headline_1 || null,
        hero_headline_2: form.hero_headline_2 || null,
        hero_headline_3: form.hero_headline_3 || null,
        hero_subhead:    form.hero_subhead    || null,
        about_h1:        form.about_h1        || null,
        about_h2:        form.about_h2        || null,
        about_body:      form.about_body      || null,
        why_h1:          form.why_h1          || null,
        why_h2:          form.why_h2          || null,
        // Diff: which fields were actually edited in this save
        changed_fields:        changedFields.join(", ") || null,
        changed_fields_count:  changedFields.length,
        hero_changed:          changedFields.some(k => k.startsWith("hero_")),
        about_changed:         changedFields.some(k => k.startsWith("about_")),
        why_changed:           changedFields.some(k => k.startsWith("why_")),
        // Previous values for changed fields only (before/after audit)
        ...Object.fromEntries(
          changedFields.map(k => [`prev_${k}`, current[k] || null])
        ),
      });
      toast.success("Site content saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const SUB_TABS = [
    { key: "customer",        label: "Customer Site" },
    { key: "store_associate", label: "Store Associate" },
  ];

  return (
    <div data-testid="admin-content-tab" className="max-w-3xl">
      <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-1">Site Settings</h2>
      <p className="text-sm text-[#2E1A12]/70 mb-4">
        Checkout behaviour and marketing text for the English version of the site.
      </p>

      <div className="flex gap-2 mb-6 border-b border-[#5A2D1D]/15" data-testid="site-settings-subtabs">
        {SUB_TABS.map((st) => (
          <button
            key={st.key}
            type="button"
            data-testid={`site-settings-subtab-${st.key}`}
            onClick={() => setSiteSettingsSubTab(st.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              siteSettingsSubTab === st.key
                ? "border-[#5A2D1D] text-[#5A2D1D]"
                : "border-transparent text-[#2E1A12]/50 hover:text-[#5A2D1D]"
            }`}
          >
            {st.label}
          </button>
        ))}
      </div>

      {siteSettingsSubTab === "store_associate" ? (
        <StoreAssociateSiteSettings />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="customer-site-subtabs">
            {CUSTOMER_SITE_SUB_TABS.map((st) => (
              <button
                key={st.key}
                type="button"
                data-testid={`customer-site-subtab-${st.key}`}
                onClick={() => setCustomerSubTab(st.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  customerSubTab === st.key
                    ? "bg-[#5A2D1D] border-[#5A2D1D] text-[#F5E6CC]"
                    : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {customerSubTab === "checkout" && (
            <>
              <CheckoutBehaviorSettings />
              <div className="mt-6">
                <PickupAddressSettings />
              </div>
            </>
          )}

          {customerSubTab === "banners" && (
            <BannerImagesAdmin />
          )}

          {customerSubTab === "pages" && (
            <>
              <div className="space-y-6">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Marketing content</div>
                {CONTENT_FIELDS.map(({ section, fields }) => (
                  <div key={section} className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">{section}</div>
                    {fields.map(({ key, label, multiline }) => (
                      <div key={key}>
                        <label className="text-xs font-semibold text-[#5A2D1D]/80">{label}</label>
                        {multiline ? (
                          <textarea
                            data-testid={`content-field-${key}`}
                            value={form[key] ?? ""}
                            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                            placeholder={DEFAULT_SITE_CONTENT[key]}
                            rows={3}
                            className="mt-1 w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm resize-none"
                          />
                        ) : (
                          <input
                            data-testid={`content-field-${key}`}
                            value={form[key] ?? ""}
                            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                            placeholder={DEFAULT_SITE_CONTENT[key]}
                            className="mt-1 w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <button
                data-testid="content-save-button"
                onClick={save}
                disabled={saving}
                className="mt-6 inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Changes
              </button>
            </>
          )}

          {customerSubTab === "legal" && (
            <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6">
              <PolicyContentAdmin />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Store-associate-facing settings — currently just the POS payment method
// toggle. Kept as its own settings sub-tab (separate from the customer-site
// checkout behavior above) since it only affects the in-person StoreBilling
// screen, not anything a customer ever sees.
const StoreAssociateSiteSettings = () => {
  const current = useCheckoutSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form && current !== DEFAULT_CHECKOUT_SETTINGS) {
      setForm({ enableStoreUpi: current.enableStoreUpi });
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div className="text-[#5A2D1D]/70">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await updateCheckoutSettings({ ...current, enableStoreUpi: form.enableStoreUpi });
      track("Saved Store Associate Settings", {
        enable_store_upi: form.enableStoreUpi,
        prev_enable_store_upi: current.enableStoreUpi,
      });
      toast.success("Store associate settings saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-6 max-w-xl">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Store Billing (POS)</div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-[#2E1A12]">Show UPI Transfer option</div>
          <div className="text-xs text-[#556B2F] mt-0.5">
            When off, store associates only see Cash as a payment option at checkout.
          </div>
        </div>
        <button
          type="button"
          data-testid="toggle-store-upi"
          onClick={() => setForm((f) => ({ ...f, enableStoreUpi: !f.enableStoreUpi }))}
          className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.enableStoreUpi ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}
        >
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.enableStoreUpi ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      <button
        data-testid="save-store-associate-settings"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-sm font-semibold disabled:opacity-60"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save Store Associate Settings
      </button>
    </div>
  );
};

const SettingsAdmin = () => {
  const current = useCheckoutSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // useCheckoutSettings() starts synchronously with the literal
    // DEFAULT_CHECKOUT_SETTINGS placeholder before the real Supabase fetch
    // resolves a moment later. Seeding from that placeholder (instead of
    // waiting for the real values) made every save silently overwrite the
    // DB back to defaults — wait for a real, distinct object before seeding.
    if (!form && current !== DEFAULT_CHECKOUT_SETTINGS) setForm(JSON.parse(JSON.stringify(current)));
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div className="text-[#5A2D1D]/70">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const newRates = Object.fromEntries(
        Object.entries(form.stateShippingRates).map(([k, v]) => [k, Number(v) || 0])
      );
      await updateCheckoutSettings({
        ...current,
        gstPercent:          Number(form.gstPercent) || 0,
        stateShippingRates:  newRates,
        enablePickup:        form.enablePickup,
        enableCourier:       form.enableCourier,
        courierNote:         form.courierNote,
      });
      const newGst  = Number(form.gstPercent) || 0;
      const prevGst = Number(current.gstPercent) || 0;
      track("Saved Taxes and Charges", {
        gst_percent:            newGst,
        state_shipping_rates:   newRates,
        enable_pickup:          form.enablePickup,
        enable_courier:         form.enableCourier,
        courier_note:           form.courierNote || null,
        gst_changed:            newGst !== prevGst,
        state_rates_changed:    JSON.stringify(newRates) !== JSON.stringify(current.stateShippingRates || {}),
        pickup_toggled:         form.enablePickup !== current.enablePickup,
        courier_toggled:        form.enableCourier !== current.enableCourier,
        courier_note_changed:   (form.courierNote || "") !== (current.courierNote || ""),
        prev_gst_percent:       prevGst,
        prev_enable_pickup:     current.enablePickup,
        prev_enable_courier:    current.enableCourier,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab" className="max-w-4xl">
      <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-6">Checkout Settings</h2>

      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-6 max-w-xl">

        <SettingRow
          label="GST (%)"
          hint="Applied on items subtotal"
          value={form.gstPercent}
          onChange={v => setForm(f => ({ ...f, gstPercent: v }))}
          suffix="%"
        />

        <div className="flex items-center justify-between gap-4 py-2 border-b border-[#5A2D1D]/10">
          <div>
            <div className="text-sm font-semibold text-[#2E1A12]">Enable Pickup on website</div>
            <div className="text-xs text-[#2E1A12]/50">Customers order online and collect when ready</div>
          </div>
          <button type="button" onClick={() => setForm(f => ({ ...f, enablePickup: !f.enablePickup }))} className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.enablePickup ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.enablePickup ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 py-2 border-b border-[#5A2D1D]/10">
          <div>
            <div className="text-sm font-semibold text-[#2E1A12]">Enable Courier Delivery on website</div>
            <div className="text-xs text-[#2E1A12]/50">Shipped via courier — not instant delivery</div>
          </div>
          <button type="button" onClick={() => setForm(f => ({ ...f, enableCourier: !f.enableCourier }))} className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${form.enableCourier ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${form.enableCourier ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Courier delivery note</label>
          <p className="text-[10px] text-[#2E1A12]/50 mb-1">Shown at checkout and on order tracking</p>
          <textarea
            value={form.courierNote || ""}
            onChange={(e) => setForm(f => ({ ...f, courierNote: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 rounded-sm border border-[#5A2D1D]/25 text-sm bg-[#F5E6CC]"
          />
        </div>

      </div>

      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 mt-6">
        <h3 className="font-display text-lg font-bold text-[#5A2D1D] mb-1">Shipping by State (₹/kg)</h3>
        <p className="text-xs text-[#2E1A12]/60 mb-4">
          This rate is a flat base charge covering any courier order up to 1kg — even a small order pays it in full, there's no minimum order weight. Orders over 1kg add a quarter of the base rate (rounded up to the nearest rupee) per extra 250g, rounded up. "Other States" is the fallback for anything not listed below. Delivery is restricted to India — customers outside India are shown a WhatsApp contact message instead of a price.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.keys(DEFAULT_STATE_SHIPPING_RATES).map((state) => (
            <div key={state} className="flex items-center justify-between gap-2 border border-[#5A2D1D]/15 rounded-sm px-3 py-2 bg-[#EFD9B4]/40">
              <span className="text-xs font-semibold text-[#2E1A12] truncate" title={state}>{state}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-[10px] text-[#556B2F] font-bold">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.stateShippingRates[state] ?? ""}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    stateShippingRates: { ...f.stateShippingRates, [state]: e.target.value },
                  }))}
                  className="w-14 px-1.5 py-1 rounded-sm border border-[#5A2D1D]/30 text-xs text-right bg-white focus:border-[#5A2D1D] focus:outline-none"
                />
                <span className="text-[10px] text-[#2E1A12]/50">/kg</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-6 w-full max-w-xl inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-3 rounded-sm font-bold transition-colors"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Save Settings
      </button>
    </div>
  );
};

const SettingRow = ({ label, hint, value, onChange, prefix, suffix }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <label className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">{label}</label>
      {hint && <span className="text-[10px] text-[#2E1A12]/50">{hint}</span>}
    </div>
    <div className="flex items-center gap-1 border border-[#5A2D1D]/30 rounded-sm overflow-hidden w-48">
      {prefix && <span className="px-2 py-2 text-sm text-[#556B2F] bg-[#EFD9B4] font-bold border-r border-[#5A2D1D]/20">{prefix}</span>}
      <input
        type="text"
        inputMode="numeric"
        // type="number" inputs have a browser quirk: setting .value to a
        // number that parses the same as what's already displayed (e.g. "01"
        // -> 1, re-rendered as 1) doesn't update the on-screen text, so a
        // leading zero typed by the admin sticks around forever. A plain
        // text input with digit filtering doesn't have that quirk.
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, ""))}
        className="flex-1 px-3 py-2 bg-[#F5E6CC] text-sm text-[#2E1A12] focus:outline-none"
      />
      {suffix && <span className="px-2 py-2 text-sm text-[#556B2F] bg-[#EFD9B4] font-bold border-l border-[#5A2D1D]/20">{suffix}</span>}
    </div>
  </div>
);

// ─── Offers Admin ───────────────────────────────────────────────────────────
const blankOffer = (scope) => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  scope,
  customer_id: null,
  customer_email: "",
  employee_name: "",
  name: "",
  code: "",
  discount_percent: 10,
  expiry_date: "",
  usage_limit: "",
  status: "active",
});

// Real fields a saveOffer() call actually persists — used to detect whether
// an existing row's draft differs from what's in the DB. Must stay in sync
// with the payload built in saveOffer() (firestoreService.js).
const OFFER_COMPARE_KEYS = ["customer_id", "customer_email", "employee_name", "name", "code", "discount_percent", "expiry_date", "usage_limit", "status"];

// True if any row that already exists in the DB (id not starting "temp-")
// has a field value that differs from its last-known-published snapshot.
const offerRowsDiffer = (rows, original) => {
  if (!rows || !original) return false;
  const originalById = new Map(original.map(r => [r.id, r]));
  return rows.some(r => {
    if (r.id.startsWith("temp-")) return false; // new rows are covered separately
    const orig = originalById.get(r.id);
    if (!orig) return false;
    return OFFER_COMPARE_KEYS.some(k => String(r[k] ?? "") !== String(orig[k] ?? ""));
  });
};

// ─── Combos & Packs Admin ───────────────────────────────────────────────────
const CombosAndPacksAdmin = () => {
  const comboCurrent = useComboSettings();
  const packCurrent  = useSamplePackSettings();
  const products = useProducts();
  const [comboForm, setComboForm] = useState(null);
  const [packForm, setPackForm]   = useState(null);
  const [savingCombo, setSavingCombo] = useState(false);
  const [savingPack, setSavingPack]   = useState(false);

  // Same "seed once from real data, not the DEFAULT placeholder" pattern as
  // SettingsAdmin/SiteContentAdmin above — compared by reference so a
  // genuinely-empty real row (still !== DEFAULT_*) seeds correctly too.
  useEffect(() => {
    if (!comboForm && comboCurrent !== DEFAULT_COMBO_SETTINGS) setComboForm(JSON.parse(JSON.stringify(comboCurrent)));
  }, [comboCurrent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!packForm && packCurrent !== DEFAULT_SAMPLE_PACK_SETTINGS) setPackForm(JSON.parse(JSON.stringify(packCurrent)));
  }, [packCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!comboForm || !packForm) return <div className="text-[#5A2D1D]/70">Loading…</div>;

  const saveCombo = async () => {
    setSavingCombo(true);
    try {
      const validProductIds = products ? new Set(products.map((p) => p.id)) : null;
      // Price inputs hold raw typed strings while editing (same reasoning as
      // SettingsAdmin's setCharge) — coerce every product's price to a clean
      // number right before save.
      const coerced = {
        ...comboForm,
        tiers: Object.fromEntries(Object.entries(comboForm.tiers).map(([k, t]) => [k, {
          ...t,
          productPrices: Object.fromEntries(
            Object.entries(t.productPrices)
              .filter(([pid]) => !validProductIds || validProductIds.has(pid))
              .map(([pid, price]) => [pid, Number(price) || 0])
          ),
        }])),
      };
      await updateComboSettings(coerced);
      track("combo_settings_saved", {
        enabled: coerced.enabled,
        eligible_product_counts: Object.fromEntries(Object.entries(coerced.tiers).map(([k, t]) => [k, Object.keys(t.productPrices).length])),
      });
      toast.success("Combo settings saved");
    } catch (e) { toast.error(e.message); } finally { setSavingCombo(false); }
  };

  const savePacks = async () => {
    setSavingPack(true);
    try {
      const validProductIds = products ? new Set(products.map((p) => p.id)) : null;
      const coerced = {
        ...packForm,
        packs: Object.fromEntries(Object.entries(packForm.packs).map(([k, p]) => [k, {
          ...p,
          maxQtyPerOrder: Number(p.maxQtyPerOrder) || 3,
          productPrices: Object.fromEntries(
            Object.entries(p.productPrices)
              .filter(([pid]) => !validProductIds || validProductIds.has(pid))
              .map(([pid, price]) => [pid, Number(price) || 0])
          ),
        }])),
      };
      await updateSamplePackSettings(coerced);
      track("sample_pack_settings_saved", {
        enabled: coerced.enabled,
        veg_product_count: Object.keys(coerced.packs.veg.productPrices).length,
        non_veg_product_count: Object.keys(coerced.packs.non_veg.productPrices).length,
      });
      toast.success("Sample pack settings saved");
    } catch (e) { toast.error(e.message); } finally { setSavingPack(false); }
  };

  return (
    <div data-testid="admin-combos-tab" className="max-w-4xl space-y-10">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-1">Combos &amp; Packs</h2>
        <p className="text-sm text-[#2E1A12]/70 mb-6">Configure "Create Your Own Combo" tiers and curated Sample Packs.</p>
      </div>

      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-6">
        <div className="flex items-center justify-between gap-4 pb-2 border-b border-[#5A2D1D]/10">
          <div>
            <div className="text-sm font-semibold text-[#2E1A12]">Enable "Create Your Own Combo"</div>
            <div className="text-xs text-[#2E1A12]/50">Shows the button on the Products page</div>
          </div>
          <button type="button" onClick={() => setComboForm(f => ({ ...f, enabled: !f.enabled }))}
            className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${comboForm.enabled ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${comboForm.enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>

        {Object.entries(comboForm.tiers).map(([tierKey, tier]) => (
          <ComboTierEditor
            key={tierKey}
            tierKey={tierKey}
            tier={tier}
            products={products || []}
            onChange={(patch) => setComboForm(f => ({ ...f, tiers: { ...f.tiers, [tierKey]: { ...f.tiers[tierKey], ...patch } } }))}
          />
        ))}

        <button onClick={saveCombo} disabled={savingCombo}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-3 rounded-sm font-bold transition-colors">
          {savingCombo && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Combo Settings
        </button>
      </div>

      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6 space-y-6">
        <div className="flex items-center justify-between gap-4 pb-2 border-b border-[#5A2D1D]/10">
          <div>
            <div className="text-sm font-semibold text-[#2E1A12]">Enable "Order Sample Pack"</div>
            <div className="text-xs text-[#2E1A12]/50">Shows the button on the Products page</div>
          </div>
          <button type="button" onClick={() => setPackForm(f => ({ ...f, enabled: !f.enabled }))}
            className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors ${packForm.enabled ? "bg-[#556B2F]" : "bg-[#5A2D1D]/25"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#F5E6CC] shadow transition-transform ${packForm.enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>

        {Object.entries(packForm.packs).map(([packKey, pack]) => (
          <SamplePackEditor
            key={packKey}
            packKey={packKey}
            pack={pack}
            products={products || []}
            onChange={(patch) => setPackForm(f => ({ ...f, packs: { ...f.packs, [packKey]: { ...f.packs[packKey], ...patch } } }))}
          />
        ))}

        <button onClick={savePacks} disabled={savingPack}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-3 rounded-sm font-bold transition-colors">
          {savingPack && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Sample Pack Settings
        </button>
      </div>
    </div>
  );
};

// Checkbox | Name | Price (editable) per product — different pickles cost
// different amounts to make, so there's no single flat tier price; checking
// a product adds it to productPrices with a price the admin sets, and the
// combo total a customer pays is the sum of whichever ones they pick (see
// create_checkout_order's combo branch, migration 030).
const ComboTierEditor = ({ tierKey, tier, products, onChange }) => {
  const toggleProduct = (id) => {
    const next = { ...tier.productPrices };
    if (id in next) delete next[id];
    else next[id] = 0;
    onChange({ productPrices: next });
  };
  const setPrice = (id, price) => onChange({ productPrices: { ...tier.productPrices, [id]: price } });
  const eligibleCount = Object.keys(tier.productPrices).length;

  return (
    <div className="border-t border-[#5A2D1D]/10 pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold text-[#5A2D1D]">{tier.label} <span className="text-xs font-normal text-[#2E1A12]/50">— {tier.itemCount} items</span></div>
        <div className="text-[10px] text-[#2E1A12]/50">{eligibleCount} products eligible</div>
      </div>
      <div className="max-h-56 overflow-y-auto border border-[#5A2D1D]/20 rounded-sm bg-white">
        {products.map(p => {
          const checked = p.id in tier.productPrices;
          return (
            <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-[#5A2D1D]/5 last:border-b-0">
              <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} />
              <span className="flex-1 truncate cursor-pointer" onClick={() => toggleProduct(p.id)}>{p.name}</span>
              <span className="text-[10px] text-[#2E1A12]/40 uppercase flex-shrink-0">{p.category}</span>
              <div className="flex items-center gap-1 border border-[#5A2D1D]/30 rounded-sm overflow-hidden flex-shrink-0">
                <span className="px-1.5 py-1 text-xs text-[#556B2F] bg-[#EFD9B4] font-bold">₹</span>
                <input
                  type="number" min="0" disabled={!checked}
                  value={checked ? tier.productPrices[p.id] : ""}
                  onChange={e => setPrice(p.id, e.target.value)}
                  className="w-16 px-2 py-1 bg-[#F5E6CC] text-xs text-[#2E1A12] focus:outline-none disabled:opacity-40"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Checkbox | Name | Price (editable) per product, same reasoning as
// ComboTierEditor — except every checked product is included in the pack
// (the customer can't deselect any), so the total shown is the sum of all of
// them, not a customer-chosen subset.
const SamplePackEditor = ({ packKey, pack, products, onChange }) => {
  const toggleProduct = (id) => {
    const next = { ...pack.productPrices };
    if (id in next) delete next[id];
    else next[id] = 0;
    onChange({ productPrices: next });
  };
  const setPrice = (id, price) => onChange({ productPrices: { ...pack.productPrices, [id]: price } });
  const productIds = Object.keys(pack.productPrices);
  const visibleProductIds = products.filter((p) => p.id in pack.productPrices).map((p) => p.id);
  const totalPrice = visibleProductIds.reduce((s, id) => s + (Number(pack.productPrices[id]) || 0), 0);
  const staleCount = Math.max(0, productIds.length - visibleProductIds.length);

  return (
    <div className="border-t border-[#5A2D1D]/10 pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="text-sm font-bold text-[#5A2D1D]">{pack.label} <span className="text-xs font-normal text-[#2E1A12]/50">— fixed {pack.fixedSizeLabel}</span></div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#556B2F] font-bold">Total: ₹{totalPrice}</span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-[#2E1A12]/60">Max/order</span>
            <input type="number" min="1" value={pack.maxQtyPerOrder} onChange={e => onChange({ maxQtyPerOrder: e.target.value })}
              className="w-14 px-2 py-2 border border-[#5A2D1D]/30 rounded-sm text-sm bg-[#F5E6CC] focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="text-[10px] text-[#2E1A12]/50 mb-1">
        {visibleProductIds.length} products in this pack
        {staleCount > 0 ? ` (${staleCount} stale config entr${staleCount === 1 ? "y" : "ies"} will be removed on save)` : ""}
      </div>
      <div className="max-h-56 overflow-y-auto border border-[#5A2D1D]/20 rounded-sm bg-white">
        {products.map(p => {
          const checked = p.id in pack.productPrices;
          return (
            <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-[#5A2D1D]/5 last:border-b-0">
              <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} />
              <span className="flex-1 truncate cursor-pointer" onClick={() => toggleProduct(p.id)}>{p.name}</span>
              <span className="text-[10px] text-[#2E1A12]/40 uppercase flex-shrink-0">{p.category}</span>
              <div className="flex items-center gap-1 border border-[#5A2D1D]/30 rounded-sm overflow-hidden flex-shrink-0">
                <span className="px-1.5 py-1 text-xs text-[#556B2F] bg-[#EFD9B4] font-bold">₹</span>
                <input
                  type="number" min="0" disabled={!checked}
                  value={checked ? pack.productPrices[p.id] : ""}
                  onChange={e => setPrice(p.id, e.target.value)}
                  className="w-16 px-2 py-1 bg-[#F5E6CC] text-xs text-[#2E1A12] focus:outline-none disabled:opacity-40"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OffersAdmin = () => {
  const allOffers = useAllOffers();
  const customers = useAllCustomers();

  // Fire once after allOffers loads — component stays mounted (CSS hidden)
  // so this runs exactly once per session when the tab is first visited.
  const offersTabEventSentRef = useRef(false);
  useEffect(() => {
    if (offersTabEventSentRef.current || allOffers === null) return;
    offersTabEventSentRef.current = true;
    const siteOffers = allOffers.filter(o => o.scope === "site");
    const userOffers = allOffers.filter(o => o.scope === "user");
    const employeeOffers = allOffers.filter(o => o.scope === "employee");
    const activeSite = siteOffers.filter(o => o.status === "active");
    const activeUser = userOffers.filter(o => o.status === "active");
    const activeEmployee = employeeOffers.filter(o => o.status === "active");
    track("Viewed Offers Tab", {
      source:                        "admin_tab_nav",
      total_offers:                  allOffers.length,
      site_offers_count:             siteOffers.length,
      user_offers_count:             userOffers.length,
      employee_offers_count:         employeeOffers.length,
      active_site_offers_count:      activeSite.length,
      active_user_offers_count:      activeUser.length,
      active_employee_offers_count:  activeEmployee.length,
      active_site_offer_names:       activeSite.map(o => o.name).join(", ") || null,
      active_site_offer_codes:       activeSite.map(o => o.code).join(", ") || null,
      active_user_offer_names:       activeUser.map(o => o.name).join(", ") || null,
      active_user_offer_codes:       activeUser.map(o => o.code).join(", ") || null,
      active_employee_offer_names:   activeEmployee.map(o => o.employee_name).join(", ") || null,
      active_employee_offer_codes:   activeEmployee.map(o => o.code).join(", ") || null,
    });
  }, [allOffers]); // eslint-disable-line react-hooks/exhaustive-deps

  const [siteRows, setSiteRows] = useState(null);
  const [userRows, setUserRows] = useState(null);
  const [employeeRows, setEmployeeRows] = useState(null);
  // Snapshot of what's actually published — rows are compared against this
  // to know whether there's anything unsaved, instead of relying on
  // transient "currently in edit mode" UI state (that broke the moment you
  // finished editing an existing row and closed its edit inputs, since
  // "currently editing" then became false even though the change was still
  // sitting unsaved in the draft rows).
  const [publishedSiteRows, setPublishedSiteRows] = useState(null);
  const [publishedUserRows, setPublishedUserRows] = useState(null);
  const [publishedEmployeeRows, setPublishedEmployeeRows] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [editingIds, setEditingIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  // Scope -> [rows, setRows] — a single lookup used everywhere below instead
  // of a three-way ternary at every call site.
  const rowsByScope = {
    site: [siteRows, setSiteRows],
    user: [userRows, setUserRows],
    employee: [employeeRows, setEmployeeRows],
  };

  // Seed local editable drafts once from the live data, then leave them
  // alone — admin's in-progress edits shouldn't get clobbered by realtime
  // pushes (including the ones caused by their own Publish below).
  useEffect(() => {
    if (allOffers === null || siteRows !== null) return;
    const site = allOffers.filter(o => o.scope === "site");
    const user = allOffers.filter(o => o.scope === "user");
    const employee = allOffers.filter(o => o.scope === "employee");
    setSiteRows(site);
    setUserRows(user);
    setEmployeeRows(employee);
    setPublishedSiteRows(site);
    setPublishedUserRows(user);
    setPublishedEmployeeRows(employee);
  }, [allOffers, siteRows]);

  const dirty = deletedIds.length > 0 ||
    (siteRows || []).some(r => r.id.startsWith("temp-")) ||
    (userRows || []).some(r => r.id.startsWith("temp-")) ||
    (employeeRows || []).some(r => r.id.startsWith("temp-")) ||
    offerRowsDiffer(siteRows, publishedSiteRows) ||
    offerRowsDiffer(userRows, publishedUserRows) ||
    offerRowsDiffer(employeeRows, publishedEmployeeRows);

  const addRow = (scope) => {
    const row = blankOffer(scope);
    rowsByScope[scope][1](rs => [row, ...rs]);
    setEditingIds(s => new Set(s).add(row.id));
  };

  const updateRow = (scope, id, patch) => {
    rowsByScope[scope][1](rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  const startEdit = (id) => setEditingIds(s => new Set(s).add(id));
  const stopEdit = (id) => setEditingIds(s => { const n = new Set(s); n.delete(id); return n; });

  const removeRow = (scope, id) => {
    rowsByScope[scope][1](rs => rs.filter(r => r.id !== id));
    stopEdit(id);
    if (!id.startsWith("temp-")) setDeletedIds(ids => [...ids, id]);
  };

  const publish = async () => {
    const allRows = [...(siteRows || []), ...(userRows || []), ...(employeeRows || [])];
    if (allRows.some(r => !r.name.trim())) {
      toast.error("Every offer needs a name before publishing.");
      return;
    }
    if (userRows.some(r => !r.customer_id)) {
      toast.error("Every user-level offer needs a customer selected before publishing.");
      return;
    }
    if (employeeRows.some(r => !r.employee_name.trim())) {
      toast.error("Every employee referral needs an employee name before publishing.");
      return;
    }
    if (employeeRows.some(r => !r.code.trim())) {
      toast.error("Every employee referral needs a redeemable code before publishing.");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(deletedIds.map(id => deleteOffer(id)));
      const saved = await Promise.all(allRows.map(saveOffer));
      track("offers_published", {
        offer_count: allRows.length,
        deleted_count: deletedIds.length,
        site_count: (siteRows || []).length,
        user_count: (userRows || []).length,
        employee_count: (employeeRows || []).length,
      });
      setDeletedIds([]);
      setEditingIds(new Set());
      // Show the just-published rows (now with real ids) immediately,
      // instead of nulling and waiting on a realtime round-trip.
      const savedSite = saved.filter(o => o.scope === "site");
      const savedUser = saved.filter(o => o.scope === "user");
      const savedEmployee = saved.filter(o => o.scope === "employee");
      setSiteRows(savedSite);
      setUserRows(savedUser);
      setEmployeeRows(savedEmployee);
      // This is the new "what's actually in the DB" baseline — dirty must
      // go back to false now that everything in it has been saved.
      setPublishedSiteRows(savedSite);
      setPublishedUserRows(savedUser);
      setPublishedEmployeeRows(savedEmployee);
      toast.success("Offers published");
    } catch (e) {
      toast.error(e.message || "Could not publish offers");
    } finally {
      setSaving(false);
    }
  };

  if (siteRows === null || userRows === null || employeeRows === null) {
    return <div className="text-[#5A2D1D]/70">Loading…</div>;
  }

  return (
    <div data-testid="admin-offers-tab" className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-1">Offers</h2>
        <p className="text-sm text-[#2E1A12]/70">
          Site-wide offers show under the bell icon for every customer. User-level offers show only for that one customer.
        </p>
      </div>

      <OffersTable
        title="Site-wide Offers"
        scope="site"
        rows={siteRows}
        editingIds={editingIds}
        onAdd={() => addRow("site")}
        onEdit={startEdit}
        onCancelEdit={stopEdit}
        onUpdate={(id, patch) => updateRow("site", id, patch)}
        onRemove={(id) => removeRow("site", id)}
      />

      <OffersTable
        title="User-level Offers"
        scope="user"
        rows={userRows}
        editingIds={editingIds}
        customers={customers}
        onAdd={() => addRow("user")}
        onEdit={startEdit}
        onCancelEdit={stopEdit}
        onUpdate={(id, patch) => updateRow("user", id, patch)}
        onRemove={(id) => removeRow("user", id)}
      />

      <div>
        <p className="text-sm text-[#2E1A12]/70 mb-3">
          Employee Referral codes are handed out personally by staff — they never appear on the customer Offers page or dropdown, and a customer can redeem at most one, ever.
        </p>
        <OffersTable
          title="Employee Referral"
          scope="employee"
          rows={employeeRows}
          editingIds={editingIds}
          onAdd={() => addRow("employee")}
          onEdit={startEdit}
          onCancelEdit={stopEdit}
          onUpdate={(id, patch) => updateRow("employee", id, patch)}
          onRemove={(id) => removeRow("employee", id)}
        />
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-[#5A2D1D]/15">
        <button
          data-testid="offers-publish"
          onClick={publish}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-6 py-3 rounded-full font-semibold transition-colors"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Publish to Site
        </button>
        {dirty && !saving && <span className="text-xs text-[#7A3B25] font-semibold">You have unpublished changes</span>}
      </div>
    </div>
  );
};

const OffersTable = ({ title, scope, rows, editingIds, customers, onAdd, onEdit, onCancelEdit, onUpdate, onRemove }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display text-lg font-bold text-[#5A2D1D]">{title}</h3>
      <button
        data-testid={`offers-add-${scope}`}
        onClick={() => { onAdd(); track("Added Offer Row", { scope }); }}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#556B2F] border border-[#556B2F]/40 px-3 py-1.5 rounded-full hover:bg-[#556B2F]/10 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add Row
      </button>
    </div>

    {rows.length === 0 ? (
      <div className="text-sm text-[#2E1A12]/50 bg-[#F5E6CC] border border-[#5A2D1D]/15 rounded-sm p-5 text-center">
        No {scope === "site" ? "site-wide" : scope === "user" ? "user-level" : "employee referral"} offers yet.
      </div>
    ) : (
      <div className="overflow-x-auto border border-[#5A2D1D]/20 rounded-sm">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-[#5A2D1D] text-[#F5E6CC]">
              {[
                ...(scope === "user" ? ["Customer"] : scope === "employee" ? ["Employee Name"] : []),
                "Offer Name", "Code", "Discount %", "Expiry Date", "Usage Limit", "Status", "Actions",
              ].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <OfferRow
                key={row.id}
                row={row}
                scope={scope}
                customers={customers}
                editing={editingIds.has(row.id)}
                striped={idx % 2 !== 0}
                onEdit={() => onEdit(row.id)}
                onCancelEdit={() => onCancelEdit(row.id)}
                onUpdate={(patch) => onUpdate(row.id, patch)}
                onRemove={() => onRemove(row.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const OfferRow = ({ row, scope, customers, editing, striped, onEdit, onCancelEdit, onUpdate, onRemove }) => {
  const rowClass = `border-b border-[#5A2D1D]/10 ${striped ? "bg-[#FAF0DC]" : "bg-[#F5E6CC]"}`;
  // Offers are only ever meant for real customers — admins never need (or
  // should be able to claim) a customer-targeted discount.
  const pickableCustomers = (customers || []).filter(c => !c.is_admin);

  if (!editing) {
    const isExpired = row.expiry_date && new Date(row.expiry_date) < new Date(new Date().toDateString());
    return (
      <tr className={rowClass}>
        {scope === "user" && (
          <td className="px-3 py-2.5 text-[#2E1A12]" data-mp-block="">{row.customer_email || "—"}</td>
        )}
        {scope === "employee" && (
          <td className="px-3 py-2.5 text-[#2E1A12]">{row.employee_name || "—"}</td>
        )}
        <td className="px-3 py-2.5 font-semibold text-[#5A2D1D]">{row.name || "—"}</td>
        <td className="px-3 py-2.5 font-mono text-xs text-[#556B2F] font-bold">{row.code || "—"}</td>
        <td className="px-3 py-2.5 text-[#2E1A12]">{row.discount_percent}%</td>
        <td className="px-3 py-2.5 text-[#2E1A12]/70 whitespace-nowrap">
          {row.expiry_date || "No expiry"}
          {isExpired && <span className="ml-1.5 text-[10px] font-bold text-red-600 uppercase">Expired</span>}
        </td>
        <td className="px-3 py-2.5 text-[#2E1A12]/70">{row.usage_limit || "Unlimited"}</td>
        <td className="px-3 py-2.5">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            row.status === "active" ? "bg-[#556B2F]/15 text-[#556B2F]" : "bg-[#5A2D1D]/10 text-[#5A2D1D]/60"
          }`}>
            {row.status}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-[#5A2D1D]/70 hover:text-[#5A2D1D]" title="Edit">
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={onRemove} className="text-[#7A3B25]/70 hover:text-[#7A3B25]" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={rowClass}>
      {scope === "user" && (
        <td className="px-3 py-2.5">
          {row.customer_id ? (
            <div className="flex items-center gap-1.5" data-mp-block="">
              <span className="text-[#2E1A12] text-xs">{row.customer_email}</span>
              <button onClick={() => onUpdate({ customer_id: null, customer_email: "" })} className="text-[#5A2D1D]/40 hover:text-[#5A2D1D]">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            // Native <select> — a custom dropdown here gets clipped by the
            // table's overflow-x-auto wrapper (it scrolls/cuts off instead
            // of floating above), a native select renders as a real OS-level
            // popup so it's never clipped.
            <select
              value=""
              onChange={(e) => {
                const c = pickableCustomers.find(c => c.id === e.target.value);
                if (c) onUpdate({ customer_id: c.id, customer_email: c.email });
              }}
              className="w-40 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
            >
              <option value="" disabled>Select customer…</option>
              {pickableCustomers.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.email} ({c.email})</option>
              ))}
            </select>
          )}
        </td>
      )}
      {scope === "employee" && (
        <td className="px-3 py-2.5">
          <input
            value={row.employee_name}
            onChange={(e) => onUpdate({ employee_name: e.target.value })}
            placeholder="e.g. Ramesh (Delivery Staff)"
            className="w-36 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
          />
        </td>
      )}
      <td className="px-3 py-2.5">
        <input
          value={row.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="e.g. Festive Discount"
          className="w-36 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          value={row.code}
          onChange={(e) => onUpdate({ code: e.target.value.toUpperCase() })}
          placeholder="e.g. FESTIVE10"
          className="w-28 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs font-mono uppercase"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          type="number" min="0" max="100"
          value={row.discount_percent}
          onChange={(e) => onUpdate({ discount_percent: e.target.value })}
          className="w-16 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          type="date"
          value={row.expiry_date || ""}
          onChange={(e) => onUpdate({ expiry_date: e.target.value })}
          className="px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
        />
      </td>
      <td className="px-3 py-2.5">
        <input
          type="number" min="0"
          value={row.usage_limit}
          onChange={(e) => onUpdate({ usage_limit: e.target.value })}
          placeholder="Unlimited"
          className="w-20 px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
        />
      </td>
      <td className="px-3 py-2.5">
        <select
          value={row.status}
          onChange={(e) => onUpdate({ status: e.target.value })}
          className="px-2 py-1.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-xs"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex gap-2">
          <button onClick={onCancelEdit} className="text-[#556B2F] hover:text-[#3f5021]" title="Done editing">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={onRemove} className="text-[#7A3B25]/70 hover:text-[#7A3B25]" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Notifications Admin ────────────────────────────────────────────────────
const NOTIF_TYPES = [
  { value: "offer",     label: "Offer",     icon: Tag,       desc: "Special deals & discounts" },
  { value: "broadcast", label: "Broadcast", icon: Megaphone, desc: "General announcements" },
  { value: "info",      label: "Info",      icon: Info,      desc: "Important information" },
];

const EMPTY_NOTIF = { type: "broadcast", title: "", body: "", link: "" };

const NotificationsAdmin = () => {
  const notifs   = useNotifications();
  const [form, setForm]     = useState(EMPTY_NOTIF);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and message are required."); return; }
    setSaving(true);
    try {
      await createNotification({ type: form.type, title: form.title.trim(), body: form.body.trim(), link: form.link.trim() || null });
      track("notification_sent_by_admin", { notification_type: form.type });
      toast.success("Notification sent to all users.");
      setForm(EMPTY_NOTIF);
    } catch (e) {
      toast.error("Failed to send notification.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeleting(id);
    try {
      await deleteNotification(id);
      track("notification_deleted_by_admin", { notification_id: id });
      toast.success("Notification deleted.");
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setDeleting(null);
    }
  };

  const TYPE_COLOR = { offer: "text-[#556B2F] bg-[#556B2F]/10", broadcast: "text-blue-600 bg-blue-50", info: "text-orange-600 bg-orange-50" };
  const TYPE_ICON  = { offer: Tag, broadcast: Megaphone, info: Info };

  return (
    <div className="space-y-8 mt-6">
      {/* Create notification */}
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6">
        <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-5 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#556B2F]" /> Send Notification
        </h2>
        <form onSubmit={send} className="space-y-4 max-w-lg">
          {/* Type selector */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-2">Type</label>
            <div className="flex gap-2 flex-wrap">
              {NOTIF_TYPES.map(t => {
                const Icon = t.icon;
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, type: t.value })); track("Changed Notification Type", { notification_type: t.value }); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                      active
                        ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                        : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <NField label="Title *" value={form.title} onChange={set("title")} placeholder="e.g. Weekend Special Offer!" />
          <NField label="Message *" value={form.body} onChange={set("body")} placeholder="e.g. Get 15% off on all pickle orders this weekend." multiline />
          <NField label="Link (optional)" value={form.link} onChange={set("link")} placeholder="e.g. /products" />

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to All Users
          </button>
        </form>
      </div>

      {/* Existing notifications */}
      <div>
        <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-4">
          Sent Notifications ({notifs.length})
        </h2>
        {notifs.length === 0 ? (
          <p className="text-sm text-[#2E1A12]/50">No notifications sent yet.</p>
        ) : (
          <div className="space-y-3">
            {notifs.map(n => {
              const Icon = TYPE_ICON[n.type] || Info;
              return (
                <div key={n.id} className="flex items-start gap-4 bg-[#F5E6CC] border border-[#5A2D1D]/15 rounded-sm px-4 py-3">
                  <span className={`inline-flex p-1.5 rounded-full text-xs font-bold mt-0.5 ${TYPE_COLOR[n.type] || "text-orange-600 bg-orange-50"}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#5A2D1D]">{n.title}</p>
                    <p className="text-xs text-[#2E1A12]/70 mt-0.5">{n.body}</p>
                    {n.link && <p className="text-xs text-[#556B2F] mt-0.5">{n.link}</p>}
                    <p className="text-[10px] text-[#2E1A12]/40 mt-1">
                      {n.createdAt?.seconds ? new Date(n.createdAt.seconds * 1000).toLocaleString("en-IN") : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(n.id)}
                    disabled={deleting === n.id}
                    className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded-sm transition-colors disabled:opacity-50"
                  >
                    {deleting === n.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Reviews Admin ──────────────────────────────────────────────────────────
const ReviewsAdmin = ({ reviews }) => {
  const [toggling, setToggling] = useState(null);

  const toggle = async (r) => {
    setToggling(r.id);
    try {
      await setReviewVisibility(r.id, !r.visible);
      track("review_visibility_toggled", { review_id: r.id, visible: !r.visible });
    } catch {
      toast.error("Failed to update review visibility.");
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="mt-6">
      <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
        <Star className="w-4 h-4 text-[#556B2F]" /> Customer Reviews ({reviews.length})
      </h2>

      {reviews.length === 0 ? (
        <p className="text-sm text-[#2E1A12]/50">No reviews submitted yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="flex items-start gap-4 bg-[#F5E6CC] border border-[#5A2D1D]/15 rounded-sm px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-[#5A2D1D]">{r.customer_name || "Customer"}</p>
                  <span className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#556B2F] text-[#556B2F]" : "text-[#5A2D1D]/20"}`}
                      />
                    ))}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${r.visible ? "bg-[#556B2F]/15 text-[#556B2F]" : "bg-[#5A2D1D]/10 text-[#5A2D1D]/60"}`}>
                    {r.visible ? "Visible" : "Hidden"}
                  </span>
                </div>
                {r.feedback && <p className="text-xs text-[#2E1A12]/70 mt-1.5">{r.feedback}</p>}
                <p className="text-[10px] text-[#2E1A12]/40 mt-1.5">
                  Order #{r.order_id?.slice(0, 8).toUpperCase()} ·{" "}
                  {r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000).toLocaleString("en-IN") : ""}
                </p>
              </div>
              <button
                onClick={() => toggle(r)}
                disabled={toggling === r.id}
                title={r.visible ? "Hide from website" : "Show on website"}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-bold border border-[#5A2D1D]/25 hover:bg-[#5A2D1D]/5 text-[#5A2D1D] px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
              >
                {toggling === r.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : r.visible ? (
                  <EyeOff className="w-3.5 h-3.5" />
                ) : (
                  <Eye className="w-3.5 h-3.5" />
                )}
                {r.visible ? "Hide" : "Show"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NField = ({ label, value, onChange, placeholder, multiline }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value} onChange={onChange} placeholder={placeholder} rows={3}
        className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] resize-none"
      />
    ) : (
      <input
        type="text" value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
      />
    )}
  </div>
);

// ─── Accounting Admin ────────────────────────────────────────────────────────
const PERIOD_OPTIONS = [
  { value: "this_month",   label: "This Month" },
  { value: "last_3months", label: "Last 3 Months" },
  { value: "this_year",    label: "This Year" },
  { value: "all",          label: "All Time" },
];

const PAID_STATUSES = ["payment_confirmed", "order_confirmed", "under_preparation", "quality_check", "ready_dispatch", "dispatched", "delivered"];
const ACTIVE_STATUSES = PAID_STATUSES;

function filterByPeriod(orders, period) {
  const now = new Date();
  return orders.filter(o => {
    const d = o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000) : null;
    if (!d) return period === "all";
    if (period === "this_month")   return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (period === "last_3months") return (now - d) <= 1000 * 60 * 60 * 24 * 92;
    if (period === "this_year")    return d.getFullYear() === now.getFullYear();
    return true;
  });
}

function monthKey(seconds) {
  if (!seconds) return "Unknown";
  const d = new Date(seconds * 1000);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

// Match an order item to a cost sheet row by name + size
function matchCost(itemName, size, costSheet) {
  const name = (itemName || "").toLowerCase();
  const row = costSheet.find(r => name.includes(r.item.toLowerCase()));
  if (!row) return null;
  const sizeKey = (size || "").replace(/(\d+)\s*gm$/i, "$1gms").replace(/(\d+)\s*gms$/i, "$1gms");
  return row[sizeKey] || null;
}

const PERIOD_TABLE_PAGE_SIZE = 6;

const AccountingAdmin = ({ orders: allOrders }) => {
  const costSheet = useCostSheet();
  const products  = useProducts();
  const [period, setPeriod] = useState("this_month");
  const [totalSort, setTotalSort] = useState(null); // null (latest first) | "desc" | "asc"
  const [periodPage, setPeriodPage] = useState(1);

  const changePeriod = (p) => { setPeriod(p); setPeriodPage(1); track("Changed Accounting Period", { period: p }); };
  const cycleTotalSort = () => {
    const next = totalSort === null ? "desc" : totalSort === "desc" ? "asc" : null;
    setTotalSort(next);
    setPeriodPage(1);
    track("Changed Accounting Sort", { sort: next || "latest_first" });
  };

  // Every derived figure here depends only on allOrders/period/costSheet —
  // memoized as one block so typing/sorting/pagination elsewhere on this
  // (large) component doesn't re-run these reduces on every render.
  const {
    orders, paidOrders, totalRevenue, totalOrders, paidCount, avgOrderValue,
    totalMaking, totalPacking, totalCost, grossProfit, profitMargin,
    monthlyMap, monthlyRows, maxMonthRev, statusMap, productMap, topProducts,
  } = useMemo(() => {
    const orders       = filterByPeriod(allOrders, period);
    const paidOrders   = orders.filter(o => PAID_STATUSES.includes(o.status));

    const totalRevenue  = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
    const totalOrders   = orders.length;
    const paidCount     = paidOrders.length;
    const avgOrderValue = paidCount > 0 ? totalRevenue / paidCount : 0;

    // Cost-sheet based profit calculations
    let totalMaking = 0, totalPacking = 0;
    paidOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const cs  = matchCost(item.name || item.product_name, item.size, costSheet);
        if (!cs) return;
        const qty = item.quantity || 1;
        totalMaking  += cs.making  * qty;
        totalPacking += cs.packing * qty;
      });
    });
    const totalCost    = totalMaking + totalPacking;
    const grossProfit  = totalRevenue - totalCost;
    const profitMargin = totalRevenue > 0 ? Math.round((grossProfit / totalRevenue) * 100) : 0;

    // Monthly breakdown (all-time data)
    const monthlyMap = {};
    allOrders.filter(o => PAID_STATUSES.includes(o.status)).forEach(o => {
      const mk = monthKey(o.createdAt?.seconds);
      if (!monthlyMap[mk]) monthlyMap[mk] = { revenue: 0, count: 0 };
      monthlyMap[mk].revenue += o.total || 0;
      monthlyMap[mk].count   += 1;
    });
    const monthlyRows = Object.entries(monthlyMap)
      .sort((a, b) => new Date("1 " + b[0]) - new Date("1 " + a[0]))
      .slice(0, 6);
    const maxMonthRev = Math.max(...monthlyRows.map(([, v]) => v.revenue), 1);

    // Status breakdown
    const statusMap = {};
    orders.forEach(o => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });

    // Top products
    const productMap = {};
    paidOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const k = item.name || item.product_name || "Unknown";
        if (!productMap[k]) productMap[k] = { qty: 0, revenue: 0 };
        productMap[k].qty     += item.quantity || 1;
        productMap[k].revenue += (item.price || 0) * (item.quantity || 1);
      });
    });
    const topProducts = Object.entries(productMap)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 5);

    return {
      orders, paidOrders, totalRevenue, totalOrders, paidCount, avgOrderValue,
      totalMaking, totalPacking, totalCost, grossProfit, profitMargin,
      monthlyMap, monthlyRows, maxMonthRev, statusMap, productMap, topProducts,
    };
  }, [allOrders, period, costSheet]);

  const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || period;

  // Fire once after orders load — AccountingAdmin mounts when the tab is first
  // visited and stays mounted (CSS hidden thereafter), so this runs exactly once
  // per session. allOrders.length as dep handles the edge case where orders
  // haven't arrived yet at mount time.
  const accountingTabEventSentRef = useRef(false);
  useEffect(() => {
    if (accountingTabEventSentRef.current || allOrders.length === 0) return;
    accountingTabEventSentRef.current = true;
    track("Viewed Accounting Tab", {
      source:             "admin_tab_nav",
      period,
      total_orders:       totalOrders,
      paid_orders:        paidCount,
      total_revenue:      Math.round(totalRevenue),
      total_cost:         Math.round(totalCost),
      total_making:       Math.round(totalMaking),
      total_packing:      Math.round(totalPacking),
      gross_profit:       Math.round(grossProfit),
      profit_margin:      profitMargin,
      avg_order_value:    Math.round(avgOrderValue),
      top_product_1:         topProducts[0]?.[0] || null,
      top_product_1_units:   topProducts[0]?.[1]?.qty || null,
      top_product_1_revenue: topProducts[0]?.[1] ? Math.round(topProducts[0][1].revenue) : null,
      top_product_2:         topProducts[1]?.[0] || null,
      top_product_2_units:   topProducts[1]?.[1]?.qty || null,
      top_product_2_revenue: topProducts[1]?.[1] ? Math.round(topProducts[1][1].revenue) : null,
      top_product_3:         topProducts[2]?.[0] || null,
      top_product_3_units:   topProducts[2]?.[1]?.qty || null,
      top_product_3_revenue: topProducts[2]?.[1] ? Math.round(topProducts[2][1].revenue) : null,
      top_product_4:         topProducts[3]?.[0] || null,
      top_product_4_units:   topProducts[3]?.[1]?.qty || null,
      top_product_5:         topProducts[4]?.[0] || null,
      top_product_5_units:   topProducts[4]?.[1]?.qty || null,
    });
  }, [allOrders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Period-table sort/pagination — hoisted out of the JSX IIFE that used to
  // recompute this (including a full array sort) on every render.
  const sorted = useMemo(() => (
    totalSort
      ? [...orders].sort((a, b) => totalSort === "desc" ? (b.total || 0) - (a.total || 0) : (a.total || 0) - (b.total || 0))
      : orders
  ), [orders, totalSort]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PERIOD_TABLE_PAGE_SIZE));
  const safePage   = Math.min(periodPage, totalPages);
  const pageOrders = useMemo(
    () => sorted.slice((safePage - 1) * PERIOD_TABLE_PAGE_SIZE, safePage * PERIOD_TABLE_PAGE_SIZE),
    [sorted, safePage]
  );
  const SortIcon = totalSort === "desc" ? ArrowDown : totalSort === "asc" ? ArrowUp : ArrowUpDown;

  const exportToExcel = async () => {
    // Loaded on demand — this library is ~90KB gzipped and only ever
    // needed here, so it shouldn't bloat the bundle every customer loads.
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { Metric: "Period",                 Value: periodLabel },
      { Metric: "Generated On",           Value: new Date().toLocaleString("en-IN") },
      { Metric: "Total Revenue (₹)",      Value: totalRevenue },
      { Metric: "Total Cost (₹)",         Value: Math.round(totalCost) },
      { Metric: "Gross Profit (₹)",       Value: Math.round(grossProfit) },
      { Metric: "Profit Margin (%)",      Value: profitMargin },
      { Metric: "Total Orders",           Value: totalOrders },
      { Metric: "Paid Orders",            Value: paidCount },
      { Metric: "Average Order Value (₹)", Value: Math.round(avgOrderValue) },
    ]), "Summary");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(orders.map(o => ({
      "Order ID":            orderNumber(o),
      "Date":                o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000).toLocaleString("en-IN") : "",
      "Customer":            o.customer_name || o.name || "",
      "Email":               o.email || "",
      "Phone":                o.phone || "",
      "Items":               (o.items || []).map(it => `${it.name} (${it.size}) x${it.quantity}`).join(", "),
      "Status":              adminStatusLabel(o.status, findStatus(o.status)?.label, o) || o.status,
      "Subtotal (₹)":        o.subtotal || 0,
      "GST %":               o.gst_percent ?? "",
      "GST Amount (₹)":      o.gst_amount || 0,
      "Discount Code":       o.offer_code || "",
      "Discount (₹)":        o.discount_amount || 0,
      "Delivery Charge (₹)": o.delivery_charge || 0,
      "Total (₹)":           o.total || 0,
      "UTR":                 o.utr || "",
      "PhonePe Txn ID":      o.phonepe_transaction_id || "",
    }))), "Orders");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      Object.entries(monthlyMap)
        .sort((a, b) => new Date("1 " + b[0]) - new Date("1 " + a[0]))
        .map(([month, data]) => ({ Month: month, "Revenue (₹)": data.revenue, "Paid Orders": data.count }))
    ), "Monthly Revenue");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      Object.entries(productMap)
        .sort((a, b) => b[1].revenue - a[1].revenue)
        .map(([name, data]) => ({ Product: name, "Units Sold": data.qty, "Revenue (₹)": data.revenue }))
    ), "Top Products");

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      Object.entries(statusMap)
        .sort((a, b) => b[1] - a[1])
        .map(([status, count]) => ({ Status: adminStatusLabel(status, findStatus(status)?.label) || status, Count: count }))
    ), "Status Breakdown");

    const dateStamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Annapurna_Accounting_${periodLabel.replace(/\s+/g, "_")}_${dateStamp}.xlsx`);
    track("Exported Accounting Report", { period, total_orders: totalOrders, total_revenue: totalRevenue });
    toast.success("Excel report downloaded");
  };

  return (
    <div className="space-y-8 mt-6">
      {/* Cost Sheet — top, shared data flows down */}
      <CostSheetTable sheet={costSheet} products={products} />

      {/* Period filter */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-[#556B2F]" />
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => changePeriod(opt.value)}
              className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                period === opt.value
                  ? "bg-[#5A2D1D] text-[#F5E6CC] border-[#5A2D1D]"
                  : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          data-testid="export-accounting-excel"
          onClick={exportToExcel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border border-[#556B2F]/40 text-[#556B2F] hover:bg-[#556B2F]/10 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Export to Excel
        </button>
      </div>

      {/* Summary cards — now profit-aware */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue",   value: formatPrice(totalRevenue),          sub: `${paidCount} paid orders`,                     icon: TrendingUp,  color: "text-[#556B2F]",  bg: "bg-[#556B2F]/10" },
          { label: "Total Cost",      value: formatPrice(Math.round(totalCost)), sub: `Making ₹${Math.round(totalMaking)} + Packing ₹${Math.round(totalPacking)}`, icon: Package, color: "text-orange-600", bg: "bg-orange-50" },
          { label: "Gross Profit",    value: formatPrice(Math.round(grossProfit)), sub: `${profitMargin}% margin`,                    icon: BarChart3,   color: grossProfit >= 0 ? "text-[#556B2F]" : "text-red-600", bg: grossProfit >= 0 ? "bg-[#556B2F]/10" : "bg-red-50" },
          { label: "Total Orders",    value: totalOrders,                         sub: `Avg ${formatPrice(Math.round(avgOrderValue))} per order`, icon: ShoppingCart, color: "text-[#5A2D1D]", bg: "bg-[#EFD9B4]/60" },
        ].map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
              <div className={`inline-flex p-2 rounded-sm ${card.bg} mb-3`}>
                <Icon className={`w-4 h-4 ${card.color}`} />
              </div>
              <div className="font-display text-2xl font-bold text-[#5A2D1D]">{card.value}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mt-1">{card.label}</div>
              <div className="text-xs text-[#2E1A12]/50 mt-0.5">{card.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly revenue chart */}
        <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          <h3 className="font-display font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#556B2F]" /> Monthly Revenue
          </h3>
          {monthlyRows.length === 0 ? (
            <p className="text-sm text-[#2E1A12]/50 py-6 text-center">No data yet</p>
          ) : (
            <div className="space-y-2.5">
              {monthlyRows.map(([month, data]) => (
                <div key={month}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-[#2E1A12]">{month}</span>
                    <span className="text-[#5A2D1D] font-bold">{formatPrice(data.revenue)}</span>
                  </div>
                  <div className="h-2 bg-[#EFD9B4] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#556B2F] rounded-full transition-all"
                      style={{ width: `${Math.max(4, (data.revenue / maxMonthRev) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-[#2E1A12]/40 mt-0.5">{data.count} orders</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top products */}
        <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          <h3 className="font-display font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-[#556B2F]" /> Top Products
          </h3>
          {topProducts.length === 0 ? (
            <p className="text-sm text-[#2E1A12]/50 py-6 text-center">No data yet</p>
          ) : (
            <div className="space-y-3">
              {topProducts.map(([name, data], idx) => (
                <div key={name} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#EFD9B4] text-[#5A2D1D] text-[10px] font-bold grid place-items-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#2E1A12] truncate">{name}</div>
                    <div className="text-xs text-[#2E1A12]/50">{data.qty} units sold</div>
                  </div>
                  <span className="text-sm font-bold text-[#5A2D1D] flex-shrink-0">{formatPrice(data.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        {/* Order status breakdown */}
        <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          <h3 className="font-display font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
            <ListOrdered className="w-4 h-4 text-[#556B2F]" /> Order Status Breakdown
          </h3>
          {Object.keys(statusMap).length === 0 ? (
            <p className="text-sm text-[#2E1A12]/50 py-6 text-center">No orders in this period</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(statusMap).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                const st = findStatus(status);
                return (
                  <div key={status} className="flex items-center justify-between py-1.5 border-b border-[#5A2D1D]/8 last:border-0">
                    <span className="text-sm text-[#2E1A12] flex items-center gap-1.5">
                      <span>{st?.icon}</span> {adminStatusLabel(status, st?.label) || status}
                    </span>
                    <span className="text-sm font-bold text-[#5A2D1D] bg-[#EFD9B4] px-2.5 py-0.5 rounded-full">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* All orders table for period */}
      <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
        <h3 className="font-display font-bold text-[#5A2D1D] mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#556B2F]" /> Orders in Period
          <span className="text-sm font-normal text-[#2E1A12]/50 ml-1">({orders.length} orders · {formatPrice(totalRevenue)} revenue)</span>
        </h3>
        {orders.length === 0 ? (
          <p className="text-sm text-[#2E1A12]/50 py-4 text-center">No orders in this period</p>
        ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#EFD9B4]/60 border-b border-[#5A2D1D]/15">
                      {["Order", "Date", "Customer", "Items", "Status"].map(h => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">{h}</th>
                      ))}
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">
                        <button onClick={cycleTotalSort} className="inline-flex items-center gap-1 hover:text-[#5A2D1D]" title="Sort by total">
                          Total <SortIcon className="w-3 h-3" />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageOrders.map((o, idx) => {
                      const st = findStatus(o.status);
                      return (
                        <tr key={o.id} className={`border-b border-[#5A2D1D]/8 hover:bg-[#EFD9B4]/30 transition-colors ${idx % 2 !== 0 ? "bg-[#F5E6CC]/60" : ""}`}>
                          <td className="px-3 py-2 font-bold text-[#5A2D1D] whitespace-nowrap">{orderNumber(o)}</td>
                          <td className="px-3 py-2 text-[#2E1A12]/60 whitespace-nowrap">
                            {o.createdAt?.seconds ? new Date(o.createdAt.seconds * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                          </td>
                          <td className="px-3 py-2 text-[#2E1A12] max-w-[120px] truncate" data-mp-block="">{o.customer_name || o.name || "—"}</td>
                          <td className="px-3 py-2 text-[#2E1A12]/70">{o.items?.length || 0}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-xs">{st?.icon} {adminStatusLabel(o.status, st?.label, o) || o.status}</span>
                          </td>
                          <td className="px-3 py-2 font-bold text-[#5A2D1D] whitespace-nowrap">{formatPrice(o.total || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#5A2D1D]/20 bg-[#EFD9B4]/40">
                      <td colSpan={5} className="px-3 py-2 font-bold text-[#5A2D1D] text-sm">Total (paid orders)</td>
                      <td className="px-3 py-2 font-display font-bold text-[#5A2D1D] text-base">{formatPrice(totalRevenue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-[#2E1A12]/50">Page {safePage} of {totalPages}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => { const p = Math.max(1, safePage - 1); setPeriodPage(p); track("Changed Accounting Orders Page", { page: p, method: "prev" }); }}
                      disabled={safePage === 1}
                      className="w-7 h-7 grid place-items-center rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { const p = Math.min(totalPages, safePage + 1); setPeriodPage(p); track("Changed Accounting Orders Page", { page: p, method: "next" }); }}
                      disabled={safePage === totalPages}
                      className="w-7 h-7 grid place-items-center rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
      </div>

    </div>
  );
};

// ─── Cost Sheet Table ────────────────────────────────────────────────────────
const SIZES = ["250gms", "500gms", "1000gms"];
const COLS  = ["making", "packing", "selling"];

function mergeProductsIntoSheet(sheetRows, products) {
  if (!products || products.length === 0) return sheetRows;
  const result = [...sheetRows];
  products.forEach(p => {
    // Normalize: strip parenthetical qualifiers so "Ginger (Allam) Pickle" → "ginger pickle"
    const pNorm = (p.name || "").replace(/\s*\([^)]*\)/g, "").trim().toLowerCase();
    const pWordCount = pNorm.split(/\s+/).filter(Boolean).length;
    const pNameLower = (p.name || "").trim().toLowerCase();
    const alreadyIn = sheetRows.some(r => {
      const rowItem = r.item.toLowerCase();
      // Exact match against the full (unstripped) product name always
      // counts as covered — needed for product lines like "Chicken Pickle
      // (Boneless)" / "Chicken Pickle (Bone-in)" whose parenthetical is the
      // *distinguishing* part, not a qualifier to ignore: both strip down to
      // the same "chicken pickle", so the substring check below can never
      // tell them apart and would otherwise re-duplicate a zero-cost row
      // for whichever one already has a real, exactly-named row.
      if (rowItem === pNameLower) return true;
      if (!pNorm.includes(rowItem)) return false;
      // Allow at most 1 extra word (e.g. "Pickle") — prevents "Chicken" row from
      // swallowing distinct products like "Chicken Karam Podi" (2 extra words)
      const rowWordCount = rowItem.split(/\s+/).filter(Boolean).length;
      return pWordCount - rowWordCount <= 1;
    });
    if (!alreadyIn) {
      result.push({
        item: p.name,
        "250gms":  { making: 0, packing: 0, selling: 0 },
        "500gms":  { making: 0, packing: 0, selling: 0 },
        "1000gms": { making: 0, packing: 0, selling: 0 },
      });
    }
  });
  return result;
}

const colLabel = { making: "Making", packing: "Packing", selling: "Selling" };

// Flattens/parses the same "Item, 250gms Making, 250gms Packing, ..." shape
// used by both the template download and the import parser, so a
// downloaded-then-reuploaded file always round-trips cleanly.
const normalizeHeader = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
const templateHeader = (size, col) => `${size} ${colLabel[col]}`;

const CostSheetTable = ({ sheet, products }) => {
  const saved = sheet;
  const [editing, setEditing] = useState(false);
  const [rows, setRows]       = useState(() => mergeProductsIntoSheet(saved, products));
  const [saving, setSaving]   = useState(false);
  const [importing, setImporting] = useState(false);

  const exportTemplate = async () => {
    const XLSX = await import("xlsx");
    const data = rows.map((r) => {
      const record = { Item: r.item };
      SIZES.forEach((s) => COLS.forEach((c) => { record[templateHeader(s, c)] = r[s]?.[c] ?? 0; }));
      return record;
    });
    const ws = XLSX.utils.json_to_sheet(data, {
      header: ["Item", ...SIZES.flatMap((s) => COLS.map((c) => templateHeader(s, c)))],
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cost Sheet");
    const dateStamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Annapurna_CostSheet_Template_${dateStamp}.xlsx`);
    track("cost_sheet_template_exported", { row_count: rows.length });
    toast.success("Template downloaded");
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const records = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!records.length) { toast.error("No rows found in file"); return; }

      // Map every normalized header ("250gmsmaking") to its {size, col} —
      // built once so parsing tolerates re-ordered/re-cased columns as long
      // as the header text itself matches the template's.
      const cellMap = {};
      SIZES.forEach((s) => COLS.forEach((c) => {
        cellMap[normalizeHeader(templateHeader(s, c))] = { size: s, col: c };
      }));
      const itemKey = Object.keys(records[0]).find((k) => normalizeHeader(k) === "item") || "Item";

      const blankSizes = () => Object.fromEntries(SIZES.map((s) => [s, { making: 0, packing: 0, selling: 0 }]));
      const parsedRows = records
        .map((record) => {
          const row = { item: String(record[itemKey] ?? "").trim(), ...blankSizes() };
          Object.entries(record).forEach(([k, v]) => {
            const cell = cellMap[normalizeHeader(k)];
            if (!cell) return;
            const num = Number(v);
            row[cell.size][cell.col] = Number.isFinite(num) ? num : 0;
          });
          return row;
        })
        .filter((r) => r.item);

      if (!parsedRows.length) { toast.error("No valid rows found — check the Item column"); return; }

      // Merge into the working table by item name (case-insensitive) rather
      // than replacing it outright, so an upload that only lists a few
      // changed items doesn't wipe out the rest of the sheet. Staged into
      // `rows` + edit mode, not saved directly — the existing Save button
      // (and its price sync) is still the one place that commits to live
      // product prices, so an upload gets the same review-before-commit step
      // as a manual edit.
      setRows((prev) => {
        const next = [...prev];
        parsedRows.forEach((pr) => {
          const idx = next.findIndex((r) => r.item.toLowerCase() === pr.item.toLowerCase());
          if (idx >= 0) next[idx] = pr; else next.push(pr);
        });
        return next;
      });
      setEditing(true);
      track("cost_sheet_imported", { row_count: parsedRows.length });
      toast.success(`Imported ${parsedRows.length} row${parsedRows.length > 1 ? "s" : ""} — review below, then click Save to apply`);
    } catch (e) {
      toast.error("Import failed: " + e.message);
    } finally {
      setImporting(false);
    }
  };

  // Sync when Firestore data loads or products change
  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (!editing) setRows(mergeProductsIntoSheet(saved, products));
  }, [saved, editing, products]);

  const setCell = (ri, size, col, val) => {
    setRows(prev => prev.map((r, i) =>
      i !== ri ? r : { ...r, [size]: { ...r[size], [col]: val === "" ? "" : Number(val) } }
    ));
  };

  const addRow = () => setRows(prev => [
    ...prev,
    { item: "New Item", "250gms": { making: 0, packing: 0, selling: 0 }, "500gms": { making: 0, packing: 0, selling: 0 }, "1000gms": { making: 0, packing: 0, selling: 0 } },
  ]);

  const removeRow = (ri) => setRows(prev => prev.filter((_, i) => i !== ri));

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCostSheet(rows);
      const synced = await syncProductPricesFromCostSheet(rows);
      track("cost_sheet_saved", { row_count: rows.length, products_synced: synced });
      setEditing(false);
      toast.success(
        synced > 0
          ? `Cost sheet saved. ${synced} product price${synced > 1 ? "s" : ""} updated.`
          : "Cost sheet saved."
      );
    } catch {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { setRows(saved); setEditing(false); };

  const colColor = { making: "text-[#2E1A12]/70", packing: "text-blue-600", selling: "text-[#556B2F] font-bold" };

  return (
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display font-bold text-[#5A2D1D] flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#556B2F]" /> Cost Sheet Reference
          <span className="text-xs font-normal text-[#2E1A12]/40">(Making · Packing · Selling — ₹)</span>
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="admin-costsheet-download-template"
            onClick={exportTemplate}
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-[#5A2D1D]/30 text-[#5A2D1D] px-4 py-1.5 rounded-full hover:bg-[#5A2D1D]/5 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download Template
          </button>
          <label
            data-testid="admin-costsheet-import"
            className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-bold border border-[#5A2D1D]/30 text-[#5A2D1D] px-4 py-1.5 rounded-full hover:bg-[#5A2D1D]/5 transition-colors"
          >
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Import Prices
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              disabled={importing}
              onChange={(e) => { handleImport(e.target.files?.[0]); e.target.value = ""; }}
            />
          </label>
          {editing ? (
            <>
              <button onClick={addRow} className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F] border border-[#556B2F]/40 px-3 py-1.5 rounded-full hover:bg-[#556B2F]/10 transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add Row
              </button>
              <button onClick={handleCancel} className="text-xs font-bold text-[#2E1A12]/50 hover:text-[#2E1A12] px-3 py-1.5 rounded-full border border-[#5A2D1D]/20 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-4 py-1.5 rounded-full transition-colors"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => { setEditing(true); track("Opened Cost Sheet Editor"); }}
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-[#5A2D1D]/30 text-[#5A2D1D] px-4 py-1.5 rounded-full hover:bg-[#5A2D1D]/5 transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#EFD9B4]/80">
              <th className="border border-[#5A2D1D]/20 px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-[#556B2F]" rowSpan={2}>Item</th>
              {SIZES.map(s => (
                <th key={s} className="border border-[#5A2D1D]/20 px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold text-[#5A2D1D]" colSpan={3}>{s}</th>
              ))}
              {editing && <th className="border border-[#5A2D1D]/20 px-2 py-2" rowSpan={2} />}
            </tr>
            <tr className="bg-[#EFD9B4]/50">
              {SIZES.map(s =>
                COLS.map(c => (
                  <th key={`${s}-${c}`} className={`border border-[#5A2D1D]/15 px-3 py-1.5 text-center text-[10px] uppercase tracking-wider font-bold ${colColor[c]}`}>
                    {colLabel[c]}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={`${ri % 2 === 0 ? "" : "bg-[#EFD9B4]/20"} hover:bg-[#EFD9B4]/40 transition-colors`}>
                <td className="border border-[#5A2D1D]/15 px-3 py-2 font-semibold text-[#2E1A12]">
                  {editing ? (
                    <input
                      value={row.item}
                      onChange={e => setRows(prev => prev.map((r, i) => i === ri ? { ...r, item: e.target.value } : r))}
                      className="w-full bg-transparent border-b border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm font-semibold text-[#2E1A12] min-w-[100px]"
                    />
                  ) : row.item}
                </td>
                {SIZES.map(size =>
                  COLS.map(col => {
                    const val = row[size]?.[col] ?? "";
                    return (
                      <td key={`${size}-${col}`} className={`border border-[#5A2D1D]/15 px-3 py-2 text-center ${colColor[col]}`}>
                        {editing ? (
                          <input
                            type="number"
                            value={val}
                            onChange={e => setCell(ri, size, col, e.target.value)}
                            className="w-16 text-center bg-transparent border-b border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
                          />
                        ) : (
                          <span>{val !== "" ? `₹${val}` : "—"}</span>
                        )}
                      </td>
                    );
                  })
                )}
                {editing && (
                  <td className="border border-[#5A2D1D]/15 px-2 py-2 text-center">
                    <button onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Wrap = ({ children }) => <div className="min-h-[60vh] grid place-items-center px-4 py-10">{children}</div>;
const Card = ({ children }) => (
  <div className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-10 max-w-md text-center">{children}</div>
);

